import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import PharmeasyLayout from "@/components/PharmeasyLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Warehouse, Plus, Edit, MapPin, Phone, Mail, User, Search, 
  CheckCircle, XCircle, Crosshair, LayoutGrid, Package, Users,
  Truck, Clock, Play, ArrowRight, Filter, Upload, ChevronRight,
  ChevronLeft, Check, ChevronDown, ChevronUp, ScanLine, Loader2, AlertTriangle,
  ExternalLink, Navigation, Download, Route, Grid3X3, Sparkles, Home, FileText, RefreshCw
} from "lucide-react";
import PincodeTripPlanner from "./PincodeTripPlanner";
import EddPicker from "./EddPicker";

const PharmeasyHubOps = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("inward");
  
  // Hubs state
  const [hubs, setHubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showHubDialog, setShowHubDialog] = useState(false);
  const [editingHub, setEditingHub] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mapsKey, setMapsKey] = useState(null);
  const [locating, setLocating] = useState(false);
  const [formData, setFormData] = useState({
    hub_name: "", address: "", city: "", state: "", pincode: "",
    coordinates: null,
    manager_name: "", manager_email: "", manager_mobile: "", manager_password: "", notes: ""
  });

  // Inward state
  const [allInwardSessions, setAllInwardSessions] = useState([]);
  const [inwardLoading, setInwardLoading] = useState(true);
  const [showHubSelectDialog, setShowHubSelectDialog] = useState(false);
  const [selectedHubId, setSelectedHubId] = useState("");

  // Wizard state
  const [showWizard, setShowWizard] = useState(false);
  const [wizardHubId, setWizardHubId] = useState(null);
  const [wizardHub, setWizardHub] = useState(null);
  const [wizardStep, setWizardStep] = useState(1);
  const [filterOptions, setFilterOptions] = useState({ paymentTypes: [], shipmentTypes: [], cities: [], pincodes: [], edds: [], ofdCounts: [], unattemptedCounts: [], receivedAtHub: [] });
  const [selectedFilters, setSelectedFilters] = useState({ paymentTypes: [], shipmentTypes: [], cities: [], pincodes: [], edds: [], presentInLastFetch: [], ofdCounts: [], unattemptedCounts: [], receivedAtHub: [] });
  const [inputMode, setInputMode] = useState("filter");
  const [bulkText, setBulkText] = useState("");
  const [bulkIdType, setBulkIdType] = useState("shipmentId");
  const [filteredCount, setFilteredCount] = useState(0);
  const [bulkValidatedCount, setBulkValidatedCount] = useState(0);
  const [bulkShipments, setBulkShipments] = useState([]);
  const [riderCount, setRiderCount] = useState("3");
  const [capacityPerRider, setCapacityPerRider] = useState("20");
  const [defaultCapacity, setDefaultCapacity] = useState(20);
  const [availableRiders, setAvailableRiders] = useState([]);
  const [selectedRiders, setSelectedRiders] = useState([]);
  const [tripRiderAssignments, setTripRiderAssignments] = useState({}); // {trip_number: rider_id}
  const [trips, setTrips] = useState([]);
  const [plan2Trips, setPlan2Trips] = useState([]);
  const [creatingTrips, setCreatingTrips] = useState(false);
  const [expandedTrips, setExpandedTrips] = useState({});
  const [locations, setLocations] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [wizardSaving, setWizardSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  const [expandedAssignments, setExpandedAssignments] = useState({});

  // Planning method state
  const [planningMethod, setPlanningMethod] = useState(""); // "route" or "pincode"
  const [pincodeSummary, setPincodeSummary] = useState([]);
  const [pincodeSummaryLoading, setPincodeSummaryLoading] = useState(false);
  const [pincodePlannedTrips, setPincodePlannedTrips] = useState([]);

  // AI Analysis state
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  // Coordinate status state
  const [coordStatus, setCoordStatus] = useState(null);
  const [coordStatusLoading, setCoordStatusLoading] = useState(false);
  const [fixingCoords, setFixingCoords] = useState(false);
  // Address status
  const [addressStatus, setAddressStatus] = useState(null);
  const [addressStatusLoading, setAddressStatusLoading] = useState(false);
  const [fixingAddresses, setFixingAddresses] = useState(false);
  // Return to hub toggle
  const [returnToHub, setReturnToHub] = useState(false);
  // Rider search
  const [riderSearch, setRiderSearch] = useState("");
  // Session detail modal
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetailLoading, setSessionDetailLoading] = useState(false);
  const [expandedSessionTrips, setExpandedSessionTrips] = useState({});

  // Scanning state
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanSessionId, setScanSessionId] = useState(null);
  const [scanCode, setScanCode] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [inwardProgress, setInwardProgress] = useState(null);
  const [selectedForBulk, setSelectedForBulk] = useState([]);
  const [bulkInwarding, setBulkInwarding] = useState(false);

  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => { 
    fetchHubs(); 
    fetchMapsKey(); 
    fetchAllInwardSessions();
  }, []);

  const fetchHubs = async () => {
    try {
      const res = await axios.get(`${API}/hubs/`);
      setHubs(res.data);
    } catch (e) { toast.error("Failed to fetch hubs"); }
    finally { setLoading(false); }
  };

  const fetchMapsKey = async () => {
    try {
      const res = await axios.get(`${API}/route-planning/maps-key`);
      setMapsKey(res.data.key);
    } catch (e) { console.error("Maps key fetch failed"); }
  };

  const fetchAllInwardSessions = async () => {
    setInwardLoading(true);
    try {
      // Fetch sessions from all hubs
      const res = await axios.get(`${API}/hubs/`);
      const hubList = res.data || [];
      
      let allSessions = [];
      for (const hub of hubList) {
        try {
          const sessionsRes = await axios.get(`${API}/inward/${hub._id}/sessions?page=1&size=50`);
          const sessions = (sessionsRes.data.data || []).map(s => ({
            ...s,
            hub_name: hub.hub_name,
            hub_city: hub.city,
          }));
          allSessions = [...allSessions, ...sessions];
        } catch (e) {
          // Hub might not have inward sessions, ignore
        }
      }
      
      // Sort by created_at descending
      allSessions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setAllInwardSessions(allSessions);
    } catch (e) {
      console.error("Failed to fetch inward sessions");
    } finally {
      setInwardLoading(false);
    }
  };

  // Wizard functions
  const startWizardForHub = async (hubId) => {
    setWizardHubId(hubId);
    setWizardStep(1);
    setShowWizard(true);
    setShowHubSelectDialog(false);
    setSelectedFilters({ paymentTypes: [], shipmentTypes: [], cities: [], pincodes: [], edds: [] });
    setTrips([]);
    setAssignments([]);
    setTripRiderAssignments({});
    setSaveResult(null);
    setExpandedTrips({});
    setPlanningMethod("");
    setPincodeSummary([]);
    setPincodePlannedTrips([]);
    
    // Fetch hub details
    const hub = hubs.find(h => h._id === hubId);
    setWizardHub(hub);
    
    // Fetch filter options
    try {
      const res = await axios.get(`${API}/inward/${hubId}/filter-options`);
      setFilterOptions(res.data);
    } catch (e) {
      console.error("Failed to fetch filter options");
    }
    
    // Fetch initial count
    fetchFilteredCount(hubId, null);
    
    // Fetch locations
    try {
      const locRes = await axios.get(`${API}/inward/${hubId}/locations`);
      setLocations(locRes.data.locations || []);
    } catch (e) {
      console.error("Failed to fetch locations");
    }
  };

  const fetchFilterOptionsWithCounts = async (hubId, filters) => {
    try {
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, values]) => {
          if (values && values.length > 0) {
            values.forEach(v => params.append(key, v));
          }
        });
      }
      const res = await axios.get(`${API}/inward/${hubId}/filter-options?${params.toString()}`);
      setFilterOptions(res.data);
    } catch (e) {
      console.error("Failed to fetch filter options");
    }
  };

  const fetchFilteredCount = async (hubId, filters) => {
    try {
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, values]) => {
          if (values && values.length > 0) {
            values.forEach(v => params.append(key, v));
          }
        });
      }
      const res = await axios.get(`${API}/inward/${hubId}/unassigned-count?${params.toString()}`);
      setFilteredCount(res.data.count || 0);
    } catch (e) {
      console.error("Failed to fetch count");
    }
  };

  // Validate bulk uploaded IDs and get actual count
  const validateBulkIds = async (hubId, ids, idType) => {
    if (!ids || ids.length === 0) {
      setBulkValidatedCount(0);
      setBulkShipments([]);
      return;
    }
    try {
      const res = await axios.post(`${API}/inward/${hubId}/validate-bulk`, {
        ids: ids,
        id_type: idType
      });
      setBulkValidatedCount(res.data.found_count || 0);
      setBulkShipments(res.data.shipments || []);
    } catch (e) {
      console.error("Failed to validate bulk IDs");
      setBulkValidatedCount(0);
      setBulkShipments([]);
    }
  };

  // Fetch default capacity and available riders
  const fetchRiderSettings = async () => {
    try {
      const [settingsRes, ridersRes] = await Promise.all([
        axios.get(`${API}/pharmeasy-orders/settings/rider-capacity`),
        axios.get(`${API}/pharmeasy-orders/riders?status=ACTIVE&size=100`)
      ]);
      
      const defCapacity = settingsRes.data.default_capacity || 20;
      setDefaultCapacity(defCapacity);
      setCapacityPerRider(String(defCapacity));
      setAvailableRiders(ridersRes.data.data || []);
    } catch (e) {
      console.error("Failed to fetch rider settings");
    }
  };

  // Fetch rider settings when wizard opens
  useEffect(() => {
    if (showWizard) {
      fetchRiderSettings();
    }
  }, [showWizard]);

  // Export session data to CSV
  const exportSessionToCSV = async (sessionId) => {
    try {
      toast.info("Preparing export...");
      const res = await axios.get(`${API}/inward/session/${sessionId}/export`);
      const { data, columns, session_code } = res.data;
      
      if (!data || data.length === 0) {
        toast.error("No data to export");
        return;
      }
      
      // Build CSV content
      const headers = [
        "Inward Session ID", "Hub", "Created At", "Created By", "Inwarded",
        "Trip Number", "Pincodes", "Shipment ID", "Package ID", "Reference ID",
        "LBN", "Address", "Slot", "Map Link", "Is Inwarded"
      ];
      
      const csvRows = [headers.join(",")];
      
      data.forEach(row => {
        const values = [
          row.inward_session_id,
          row.hub,
          row.created_at,
          row.created_by,
          row.inwarded_count,
          row.trip_number,
          `"${(row.pincodes || '').replace(/"/g, '""')}"`,
          row.shipment_id,
          row.package_id,
          `"${(row.reference_id || '').replace(/"/g, '""')}"`,
          row.lbn,
          `"${(row.address || '').replace(/"/g, '""')}"`,
          row.slot,
          `"${(row.map_link || '').replace(/"/g, '""')}"`,
          row.is_inwarded ? "Yes" : "No"
        ];
        csvRows.push(values.join(","));
      });
      
      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement("a");
      link.href = url;
      link.download = `inward_session_${session_code}_export.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${data.length} rows successfully`);
    } catch (e) {
      console.error("Export failed:", e);
      toast.error("Failed to export session data");
    }
  };

  const handleFilterChange = (filterType, value, checked) => {
    setSelectedFilters(prev => {
      const updated = { ...prev };
      if (checked) {
        updated[filterType] = [...(prev[filterType] || []), value];
      } else {
        updated[filterType] = (prev[filterType] || []).filter(v => v !== value);
      }
      return updated;
    });
  };

  // Auto-refresh filtered count and filter options when filters change
  useEffect(() => {
    if (showWizard && wizardHubId && inputMode === "filter") {
      const filters = {};
      Object.keys(selectedFilters).forEach(key => {
        if (selectedFilters[key] && selectedFilters[key].length > 0) {
          filters[key] = selectedFilters[key];
        }
      });
      const activeFilters = Object.keys(filters).length > 0 ? filters : null;
      fetchFilteredCount(wizardHubId, activeFilters);
      fetchFilterOptionsWithCounts(wizardHubId, activeFilters);
    }
  }, [selectedFilters, showWizard, inputMode, wizardHubId]);

  // Fetch address + coordinate status when entering Step 3
  useEffect(() => {
    if (wizardStep === 3 && wizardHubId) {
      fetchAddressStatus();
      fetchCoordinateStatus();
    }
  }, [wizardStep, wizardHubId]);



  const runAiPlanningAnalysis = async () => {
    setAiAnalyzing(true);
    setAiAnalysis(null);
    try {
      const requestData = {};
      if (inputMode === "bulk" && bulkUploadIds.length > 0) {
        requestData.bulk_ids = bulkUploadIds;
        requestData.bulk_id_type = bulkIdType;
      } else {
        requestData.filters = selectedFilters;
      }
      const res = await axios.post(`${API}/inward/${wizardHubId}/ai-analyze-shipments`, requestData);
      setAiAnalysis(res.data);
      if (res.data.error) {
        toast.error(`AI Analysis: ${res.data.error}`);
      } else {
        toast.success("AI planning analysis complete");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI analysis failed");
      setAiAnalysis({ error: e.response?.data?.detail || "Failed" });
    } finally {
      setAiAnalyzing(false);
    }
  };



  const fetchCoordinateStatus = async () => {
    if (!wizardHubId) return;
    setCoordStatusLoading(true);
    try {
      const requestData = {};
      if (inputMode === "bulk" && bulkShipments.length > 0) {
        requestData.shipment_ids = bulkShipments.map(s => s.shipmentId);
        requestData.id_type = "shipmentId";
      } else {
        requestData.filters = selectedFilters;
      }
      const res = await axios.post(`${API}/inward/${wizardHubId}/coordinate-status`, requestData);
      setCoordStatus(res.data);
    } catch (e) {
      console.error("Coord status error:", e);
    } finally {
      setCoordStatusLoading(false);
    }
  };

  const fixMissingCoordinates = async () => {
    if (!wizardHubId || !coordStatus?.missing_ids?.length) return;
    setFixingCoords(true);
    try {
      const res = await axios.post(`${API}/inward/${wizardHubId}/fix-coordinates`, {
        shipment_ids: coordStatus.missing_ids,
        limit: coordStatus.missing_ids.length,
      });
      const data = res.data;
      toast.success(`Geocoded ${data.success_count}/${data.total_processed} shipments`);
      // Refresh status
      await fetchCoordinateStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to fix coordinates");
    } finally {
      setFixingCoords(false);
    }
  };

  const fetchAddressStatus = async () => {
    if (!wizardHubId) return;
    setAddressStatusLoading(true);
    try {
      const requestData = {};
      if (inputMode === "bulk" && bulkShipments.length > 0) {
        requestData.shipment_ids = bulkShipments.map(s => s.shipmentId);
        requestData.id_type = "shipmentId";
      } else {
        requestData.filters = selectedFilters;
      }
      const res = await axios.post(`${API}/inward/${wizardHubId}/address-status`, requestData);
      setAddressStatus(res.data);
    } catch (e) {
      console.error("Address status error:", e);
    } finally {
      setAddressStatusLoading(false);
    }
  };

  const fixMissingAddresses = async () => {
    if (!wizardHubId || !addressStatus?.missing_ids?.length) return;
    setFixingAddresses(true);
    try {
      const res = await axios.post(`${API}/inward/${wizardHubId}/fix-addresses`, {
        shipment_ids: addressStatus.missing_ids,
      });
      const data = res.data;
      toast.success(`Fetched details for ${data.success_count}/${data.total_processed} shipments (${data.geocoded_count} geocoded)`);
      // Refresh both address and coordinate status
      await fetchAddressStatus();
      await fetchCoordinateStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to fix addresses");
    } finally {
      setFixingAddresses(false);
    }
  };


  const createTrips = async () => {
    setCreatingTrips(true);
    try {
      const requestData = {
        hub_id: wizardHubId,
        max_shipments_per_trip: parseInt(capacityPerRider) || 25,
        num_riders: parseInt(riderCount) || null,
        return_to_hub: returnToHub,
      };
      
      if (inputMode === "bulk" && bulkText.trim()) {
        requestData.bulk_ids = bulkText.split(/[\n,\s]+/).map(id => id.trim()).filter(id => id);
        requestData.bulk_id_type = bulkIdType;
      } else {
        const filters = {};
        Object.keys(selectedFilters).forEach(key => {
          if (selectedFilters[key] && selectedFilters[key].length > 0) {
            filters[key] = selectedFilters[key];
          }
        });
        if (Object.keys(filters).length > 0) {
          requestData.filters = filters;
        }
      }
      
      const res = await axios.post(`${API}/inward/${wizardHubId}/create-trips`, requestData);
      setTrips(res.data.trips || []);
      setPlan2Trips(res.data.plan2_trips || []);
      toast.success(`Created ${res.data.total_trips} trips with ${res.data.total_shipments} shipments`);
      setWizardStep(5);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create trips");
    } finally {
      setCreatingTrips(false);
    }
  };

  const assignTripsToLocations = async () => {
    if (trips.length > locations.length) {
      toast.error(`Not enough locations. Need ${trips.length}, have ${locations.length}`);
      return;
    }
    
    setAssigning(true);
    try {
      const tripAssignments = trips.map((trip, idx) => ({
        trip_number: trip.trip_number,
        pincodes: trip.pincodes,
        pincodes_display: trip.pincodes_display,
        pincode: trip.pincodes?.[0] || trip.pincode,  // For historical matching
        location_id: locations[idx]?._id,
        location_name: locations[idx]?.location_name,
        shipments: trip.shipments,
      }));
      
      const res = await axios.post(`${API}/inward/${wizardHubId}/assign-trips`, {
        hub_id: wizardHubId,
        trips: tripAssignments,
        plan2_trips: plan2Trips.length > 0 ? plan2Trips.map((trip, idx) => ({
          trip_number: trip.trip_number,
          shipment_count: trip.shipment_count,
          pincodes: trip.pincodes,
          pincodes_display: trip.pincodes_display,
          pincode: trip.pincodes?.[0],
          location_id: locations[idx]?._id,
          location_name: locations[idx]?.location_name,
          shipments: trip.shipments,
        })) : null,
        selected_rider_ids: selectedRiders.length > 0 ? selectedRiders : null,
      });
      
      const assignmentsData = res.data.assignments || [];
      const plan2AssignmentsData = res.data.plan2_assignments || [];
      
      // Initialize trip rider assignments based on selectedRiders order
      const initialRiderAssignments = {};
      assignmentsData.forEach((assignment, idx) => {
        const riderId = assignment.rider_id || (selectedRiders[idx] || null);
        if (riderId) {
          initialRiderAssignments[assignment.trip_number] = riderId;
        }
      });
      
      toast.success("Trips assigned to locations");

      // Close the wizard modal and navigate to full preview page
      setShowWizard(false);
      navigate("/pharmeasy/hub-ops/inward-preview", {
        state: {
          assignments: assignmentsData,
          plan2Assignments: plan2AssignmentsData,
          trips,
          plan2Trips,
          availableRiders,
          wizardHubId,
          wizardHub,
          selectedFilters,
          riderCount,
          capacityPerRider,
          inputMode,
          bulkShipments,
          selectedRiders,
          initialRiderAssignments,
        },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to assign locations");
    } finally {
      setAssigning(false);
    }
  };

  const saveWizardInward = async () => {
    // Validate no duplicate rider assignments
    const usedRiders = Object.values(tripRiderAssignments).filter(Boolean);
    const duplicates = usedRiders.filter((id, idx) => usedRiders.indexOf(id) !== idx);
    if (duplicates.length > 0) {
      const dupNames = [...new Set(duplicates)].map(id => {
        const rider = availableRiders.find(r => r._id === id);
        return rider?.name || id;
      });
      toast.error(`Cannot save: Duplicate rider assignments detected (${dupNames.join(", ")})`);
      return;
    }
    
    setWizardSaving(true);
    try {
      // Enrich assignments with rider info
      const enrichedAssignments = assignments.map(assignment => ({
        ...assignment,
        rider_id: tripRiderAssignments[assignment.trip_number] || assignment.rider_id || null,
      }));
      
      const res = await axios.post(`${API}/inward/${wizardHubId}/save`, {
        hub_id: wizardHubId,
        rider_count: parseInt(riderCount) || 0,
        capacity_per_rider: parseInt(capacityPerRider) || 0,
        filters_used: selectedFilters,
        assignments: enrichedAssignments,
      });
      
      setSaveResult(res.data);
      toast.success(`Inward Session ${res.data.session_code} created!`);
      // Note: Session list will refresh when wizard closes
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save inward session");
    } finally {
      setWizardSaving(false);
    }
  };

  const totalWizardSteps = 5;
  const nextStep = () => setWizardStep(s => Math.min(s + 1, totalWizardSteps));
  const prevStep = () => setWizardStep(s => Math.max(s - 1, 1));

  // Fetch pincode summary for pincode planning method
  const fetchPincodeSummary = async () => {
    setPincodeSummaryLoading(true);
    try {
      const requestData = { hub_id: wizardHubId };
      if (inputMode === "bulk" && bulkText.trim()) {
        requestData.bulk_ids = bulkText.split(/[\n,\s]+/).map(id => id.trim()).filter(id => id);
        requestData.bulk_id_type = bulkIdType;
      } else {
        const filters = {};
        Object.keys(selectedFilters).forEach(key => {
          if (selectedFilters[key] && selectedFilters[key].length > 0) {
            filters[key] = selectedFilters[key];
          }
        });
        if (Object.keys(filters).length > 0) {
          requestData.filters = filters;
        }
      }
      const res = await axios.post(`${API}/inward/${wizardHubId}/pincode-summary`, requestData);
      setPincodeSummary(res.data.pincodes || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to fetch pincode summary");
    } finally {
      setPincodeSummaryLoading(false);
    }
  };

  // Create trips from pincode plans
  const createPincodeTrips = async () => {
    if (pincodePlannedTrips.length === 0) {
      toast.error("No trips planned");
      return;
    }
    setCreatingTrips(true);
    try {
      const requestData = { hub_id: wizardHubId };
      if (inputMode === "bulk" && bulkText.trim()) {
        requestData.bulk_ids = bulkText.split(/[\n,\s]+/).map(id => id.trim()).filter(id => id);
        requestData.bulk_id_type = bulkIdType;
      } else {
        const filters = {};
        Object.keys(selectedFilters).forEach(key => {
          if (selectedFilters[key] && selectedFilters[key].length > 0) {
            filters[key] = selectedFilters[key];
          }
        });
        if (Object.keys(filters).length > 0) {
          requestData.filters = filters;
        }
      }
      requestData.plans = pincodePlannedTrips.map(t => ({
        pincodes: t.pincodes,
        rider_id: t.rider_id,
        splits: t.splits,
      }));

      const res = await axios.post(`${API}/inward/${wizardHubId}/create-pincode-trips`, requestData);
      const createdTrips = res.data.trips || [];
      setTrips(createdTrips);

      // Pre-populate rider assignments from plans
      const riderAssignments = {};
      createdTrips.forEach((trip, idx) => {
        if (trip.rider_id) {
          riderAssignments[trip.trip_number] = trip.rider_id;
        } else if (pincodePlannedTrips[idx]?.rider_id) {
          riderAssignments[trip.trip_number] = pincodePlannedTrips[idx].rider_id;
        }
      });
      setTripRiderAssignments(riderAssignments);

      // Pre-populate selectedRiders from pincode plans
      const riderIds = pincodePlannedTrips.map(t => t.rider_id).filter(Boolean);
      setSelectedRiders([...new Set(riderIds)]);
      setRiderCount(String(createdTrips.length));

      toast.success(`Created ${createdTrips.length} optimized trips with ${res.data.total_shipments} shipments`);
      setWizardStep(5); // Skip to assign & save
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create trips");
    } finally {
      setCreatingTrips(false);
    }
  };

  // Session detail functions
  const fetchSessionDetail = async (session) => {
    setSessionDetailLoading(true);
    try {
      const res = await axios.get(`${API}/inward/${session.hub_id}/sessions/${session._id}`);
      setSelectedSession({ ...res.data, hub_name: session.hub_name });
      setExpandedSessionTrips({});
    } catch (e) {
      toast.error("Failed to load session details");
    } finally {
      setSessionDetailLoading(false);
    }
  };

  // Scanning functions
  const startInwardScanning = async (session) => {
    setScanSessionId(session._id);
    setScanCode("");
    setScanResult(null);
    setSelectedForBulk([]);
    setShowScanModal(true);
    setSelectedSession(null);
    
    try {
      const res = await axios.get(`${API}/inward/${session.hub_id}/sessions/${session._id}/progress`);
      setInwardProgress({ ...res.data, hub_id: session.hub_id });
    } catch (e) {
      console.error("Failed to fetch progress");
    }
  };

  const getDeviceCoordinates = () => {
    return new Promise((resolve) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          () => resolve(null),
          { timeout: 5000 }
        );
      } else {
        resolve(null);
      }
    });
  };

  const handleScan = async () => {
    if (!scanCode.trim() || !inwardProgress?.hub_id || scanning) return;
    
    setScanning(true);
    setScanResult(null);
    
    try {
      const coords = await getDeviceCoordinates();
      const res = await axios.post(`${API}/inward/${inwardProgress.hub_id}/sessions/${scanSessionId}/scan`, {
        scan_code: scanCode.trim(),
        device_coordinates: coords,
      });
      
      setScanResult({ success: true, ...res.data });
      
      const progressRes = await axios.get(`${API}/inward/${inwardProgress.hub_id}/sessions/${scanSessionId}/progress`);
      setInwardProgress({ ...progressRes.data, hub_id: inwardProgress.hub_id });
      setScanCode("");
      
      if (res.data.all_inwarded) {
        fetchAllInwardSessions();
      }
    } catch (e) {
      setScanResult({ success: false, error: e.response?.data?.detail || "Scan failed" });
    } finally {
      setScanning(false);
    }
  };

  const handleBulkInward = async () => {
    if (selectedForBulk.length === 0 || !inwardProgress?.hub_id || bulkInwarding) return;
    
    setBulkInwarding(true);
    try {
      const coords = await getDeviceCoordinates();
      const res = await axios.post(`${API}/inward/${inwardProgress.hub_id}/sessions/${scanSessionId}/bulk-inward`, {
        shipment_ids: selectedForBulk,
        device_coordinates: coords,
      });
      
      toast.success(`Inwarded ${res.data.inwarded_count} shipments`);
      
      const progressRes = await axios.get(`${API}/inward/${inwardProgress.hub_id}/sessions/${scanSessionId}/progress`);
      setInwardProgress({ ...progressRes.data, hub_id: inwardProgress.hub_id });
      setSelectedForBulk([]);
      
      if (res.data.all_inwarded) {
        fetchAllInwardSessions();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk inward failed");
    } finally {
      setBulkInwarding(false);
    }
  };

  // Google Maps
  useEffect(() => {
    if (!mapsKey || document.getElementById("google-maps-script")) return;
    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${mapsKey}&libraries=geometry`;
    script.async = true;
    document.body.appendChild(script);
  }, [mapsKey]);

  const initMap = useCallback(() => {
    if (!mapContainerRef.current || !window.google) return;
    const defaultCenter = formData.coordinates
      ? { lat: formData.coordinates.lat, lng: formData.coordinates.lng }
      : { lat: 20.5937, lng: 78.9629 };
    const defaultZoom = formData.coordinates ? 15 : 5;

    const map = new window.google.maps.Map(mapContainerRef.current, {
      zoom: defaultZoom, center: defaultCenter, mapTypeControl: false, streetViewControl: false,
    });
    mapInstanceRef.current = map;

    const marker = new window.google.maps.Marker({
      position: defaultCenter, map, draggable: true, visible: !!formData.coordinates,
    });
    markerRef.current = marker;

    map.addListener("click", (e) => {
      const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      marker.setPosition(pos); marker.setVisible(true);
      setFormData((prev) => ({ ...prev, coordinates: pos }));
      reverseGeocode(pos);
    });
    marker.addListener("dragend", () => {
      const pos = { lat: marker.getPosition().lat(), lng: marker.getPosition().lng() };
      setFormData((prev) => ({ ...prev, coordinates: pos }));
      reverseGeocode(pos);
    });
  }, [formData.coordinates]);

  const reverseGeocode = (pos) => {
    if (!window.google) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: pos }, (results, status) => {
      if (status === "OK" && results[0]) {
        let city = "", state = "", pincode = "";
        results[0].address_components.forEach((c) => {
          if (c.types.includes("locality")) city = c.long_name;
          if (c.types.includes("administrative_area_level_1")) state = c.long_name;
          if (c.types.includes("postal_code")) pincode = c.long_name;
        });
        setFormData((prev) => ({
          ...prev,
          address: results[0].formatted_address || prev.address,
          city: city || prev.city, state: state || prev.state, pincode: pincode || prev.pincode,
        }));
      }
    });
  };

  const locateAddress = () => {
    if (!formData.address.trim()) { toast.error("Please enter an address first"); return; }
    if (!window.google) { toast.error("Map is still loading"); return; }
    setLocating(true);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: formData.address, componentRestrictions: { country: "IN" } }, (results, status) => {
      setLocating(false);
      if (status === "OK" && results[0]) {
        const loc = results[0].geometry.location;
        const pos = { lat: loc.lat(), lng: loc.lng() };
        if (mapInstanceRef.current) { mapInstanceRef.current.setCenter(pos); mapInstanceRef.current.setZoom(15); }
        if (markerRef.current) { markerRef.current.setPosition(pos); markerRef.current.setVisible(true); }
        let city = "", state = "", pincode = "";
        results[0].address_components.forEach((c) => {
          if (c.types.includes("locality")) city = c.long_name;
          if (c.types.includes("administrative_area_level_1")) state = c.long_name;
          if (c.types.includes("postal_code")) pincode = c.long_name;
        });
        setFormData((prev) => ({
          ...prev, city: city || prev.city, state: state || prev.state, pincode: pincode || prev.pincode, coordinates: pos,
        }));
        toast.success("Location found! Drag marker to adjust if needed.");
      } else {
        toast.error("Could not find location. Try a more specific address or click on the map.");
      }
    });
  };

  useEffect(() => {
    if (showHubDialog && window.google) {
      const timer = setTimeout(initMap, 200);
      return () => clearTimeout(timer);
    }
  }, [showHubDialog, initMap]);

  // Hub CRUD handlers
  const openAddHub = () => {
    setEditingHub(null);
    setFormData({
      hub_name: "", address: "", city: "", state: "", pincode: "",
      coordinates: null, manager_name: "", manager_email: "", manager_mobile: "", manager_password: "", notes: ""
    });
    setShowHubDialog(true);
  };

  const openEditHub = (hub) => {
    setEditingHub(hub);
    setFormData({
      hub_name: hub.hub_name || "", address: hub.address || "", city: hub.city || "",
      state: hub.state || "", pincode: hub.pincode || "", coordinates: hub.coordinates || null,
      manager_name: hub.manager_name || "", manager_email: hub.manager_email || "",
      manager_mobile: hub.manager_mobile || "", manager_password: "", notes: hub.notes || "",
    });
    setShowHubDialog(true);
  };

  const handleHubSubmit = async (e) => {
    e.preventDefault();
    if (!formData.hub_name || !formData.address || !formData.manager_name || !formData.manager_mobile) {
      toast.error("Hub name, address, manager name and mobile are required"); return;
    }
    if (!editingHub && (!formData.manager_email || !formData.manager_password)) {
      toast.error("Manager email and password are required for new hubs"); return;
    }
    if (!editingHub && formData.manager_password.length < 6) {
      toast.error("Password must be at least 6 characters"); return;
    }
    setSaving(true);
    try {
      if (editingHub) {
        const { manager_password, ...updateData } = formData;
        await axios.put(`${API}/hubs/${editingHub._id}`, updateData);
        toast.success("Hub updated successfully");
      } else {
        await axios.post(`${API}/hubs/`, formData);
        toast.success("Hub created successfully. Manager account created.");
      }
      setShowHubDialog(false);
      fetchHubs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save hub");
    } finally { setSaving(false); }
  };

  const handleDeleteHub = async (hubId) => {
    if (!window.confirm("Are you sure you want to delete this hub?")) return;
    try {
      await axios.delete(`${API}/hubs/${hubId}`);
      toast.success("Hub deleted");
      fetchHubs();
    } catch (err) { toast.error("Failed to delete hub"); }
  };

  // Inward handlers
  const openNewInward = () => {
    if (hubs.length === 0) {
      toast.error("No hubs available. Please add a hub first.");
      setActiveTab("hub");
      return;
    }
    setSelectedHubId("");
    setShowHubSelectDialog(true);
  };

  const proceedToInward = () => {
    if (!selectedHubId) {
      toast.error("Please select a hub");
      return;
    }
    startWizardForHub(selectedHubId);
  };

  const filtered = hubs.filter((h) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return h.hub_name?.toLowerCase().includes(q) || h.city?.toLowerCase().includes(q) || h.manager_name?.toLowerCase().includes(q);
  });

  const formatDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  // Generate Google Maps link from coordinates
  // Priority: calculatedDestAddressCoordinate > destAddress.latitude/longitude
  const getMapLink = (shipment) => {
    // First check calculated coordinates (more accurate)
    const calcCoords = shipment?.calculatedDestAddressCoordinate || shipment?.calculated_dest_address_coordinate;
    if (calcCoords?.success && calcCoords?.coordinates) {
      return `https://www.google.com/maps?q=${calcCoords.coordinates.lat},${calcCoords.coordinates.lng}`;
    }
    
    // Fall back to destAddress (may be inaccurate)
    const destAddr = shipment?.destAddress || shipment?.dest_address;
    if (destAddr?.latitude && destAddr?.longitude) {
      const lat = parseFloat(destAddr.latitude);
      const lng = parseFloat(destAddr.longitude);
      if (lat !== 0 && lng !== 0 && !isNaN(lat) && !isNaN(lng)) {
        return `https://www.google.com/maps?q=${lat},${lng}`;
      }
    }
    return null;
  };

  // Format address for display
  const formatAddress = (destAddr) => {
    if (!destAddr) return "—";
    const parts = [destAddr.address1, destAddr.city, destAddr.pincode].filter(Boolean);
    return parts.join(", ") || "—";
  };

  return (
    <PharmeasyLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="pharmeasy-hub-ops">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-slate-800 mb-2">Hub Operations</h1>
            <p className="text-slate-600">Manage Pharmeasy hub locations, layouts and inward operations</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="inward" className="flex items-center gap-2">
              <Package className="w-4 h-4" /> Inward
            </TabsTrigger>
            <TabsTrigger value="hub" className="flex items-center gap-2">
              <Warehouse className="w-4 h-4" /> Hub
            </TabsTrigger>
          </TabsList>

          {/* Inward Tab */}
          <TabsContent value="inward" className="space-y-6 mt-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="border-0 shadow-md">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <Truck className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">{allInwardSessions.length}</p>
                      <p className="text-xs text-slate-500">Total Inward Sessions</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-md">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Package className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">
                        {allInwardSessions.reduce((sum, s) => sum + (s.total_shipments || 0), 0)}
                      </p>
                      <p className="text-xs text-slate-500">Total Shipments Inwarded</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-md">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                      <Warehouse className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">{hubs.length}</p>
                      <p className="text-xs text-slate-500">Active Hubs</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-md">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">
                        {allInwardSessions[0] ? formatDate(allInwardSessions[0].created_at).split(",")[0] : "—"}
                      </p>
                      <p className="text-xs text-slate-500">Last Inward</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* New Inward Button */}
            <div className="flex justify-end">
              <Button onClick={openNewInward} style={{ backgroundColor: "#1B8C3D" }} data-testid="new-inward-btn">
                <Play className="w-4 h-4 mr-2" /> New Inward
              </Button>
            </div>

            {/* Inward History */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-slate-600" />
                  Inward History
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {inwardLoading ? (
                  <div className="text-center py-8 text-slate-500">Loading inward sessions...</div>
                ) : allInwardSessions.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-lg font-medium text-slate-500 mb-1">No inward sessions yet</p>
                    <p className="text-sm text-slate-400 mb-4">Click "New Inward" to start</p>
                    <Button onClick={openNewInward} style={{ backgroundColor: "#1B8C3D" }}>
                      <Play className="w-4 h-4 mr-2" /> New Inward
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Session ID</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Hub</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Created By</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Trips</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Shipments</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {allInwardSessions.map((session) => (
                          <tr key={session._id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/pharmeasy/hub-ops/session/${session._id}`)}>
                            <td className="px-4 py-3 align-middle">
                              <span className="font-mono text-sm font-medium text-blue-600">{session.session_code || `INW${session._id?.slice(-4)}`}</span>
                            </td>
                            <td className="px-4 py-3 align-middle text-sm text-slate-600">
                              <div className="whitespace-nowrap">
                                <div>{formatDate(session.created_at).split(",")[0]}</div>
                                <div className="text-xs text-slate-500">{formatDate(session.created_at).split(",")[1]?.trim()}</div>
                              </div>
                            </td>
                            <td className="px-4 py-3 align-middle">
                              <div className="text-sm font-medium text-slate-800">{session.hub_name}</div>
                              <div className="text-xs text-slate-500">{session.hub_city}</div>
                            </td>
                            <td className="px-4 py-3 align-middle text-sm text-slate-800">{session.created_by}</td>
                            <td className="px-4 py-3 align-middle text-right text-sm font-medium text-slate-800">{session.total_trips}</td>
                            <td className="px-4 py-3 align-middle text-right text-sm font-medium text-blue-600">{session.total_shipments}</td>
                            <td className="px-4 py-3 align-middle">
                              <Badge className={session.status === "COMPLETED" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                                {session.status}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 align-middle">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={(e) => { e.stopPropagation(); navigate(`/pharmeasy/hub-ops/session/${session._id}`); }}
                              >
                                View
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Hub Tab */}
          <TabsContent value="hub" className="space-y-6 mt-6">
            {/* Add Hub Button */}
            <div className="flex justify-end">
              <Button onClick={openAddHub} style={{ backgroundColor: "#1B8C3D" }} data-testid="add-hub-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Hub
              </Button>
            </div>

            {/* Search */}
            <Card className="border-0 shadow-lg">
              <CardContent className="pt-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Search hubs by name, city or manager..." value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="hub-search-input" />
                </div>
              </CardContent>
            </Card>

            {/* Hubs List */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center text-slate-600">Loading hubs...</div>
                ) : filtered.length === 0 ? (
                  <div className="p-12 text-center">
                    <Warehouse className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-lg font-medium text-slate-500 mb-1">
                      {searchQuery ? "No hubs match your search" : "No hubs added yet"}
                    </p>
                    <p className="text-sm text-slate-400 mb-4">
                      {searchQuery ? "Try a different search term" : "Click Add Hub to create your first hub"}
                    </p>
                    {!searchQuery && (
                      <Button onClick={openAddHub} style={{ backgroundColor: "#1B8C3D" }}>
                        <Plus className="w-4 h-4 mr-2" /> Add Hub
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Hub Name</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Location</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Manager</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Contact</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Coordinates</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {filtered.map((hub) => (
                          <tr key={hub._id} className="hover:bg-slate-50" data-testid={`hub-row-${hub._id}`}>
                            <td className="px-4 py-4 align-middle">
                              <span className="text-sm font-medium text-slate-900">{hub.hub_name}</span>
                            </td>
                            <td className="px-4 py-4 align-middle">
                              <div className="text-sm text-slate-600 max-w-xs truncate">{hub.address}</div>
                              <div className="text-xs text-slate-400 mt-0.5">{[hub.city, hub.state, hub.pincode].filter(Boolean).join(", ")}</div>
                            </td>
                            <td className="px-4 py-4 align-middle">
                              <div className="flex items-center gap-1.5">
                                <User className="w-3.5 h-3.5 text-slate-400" />
                                <span className="text-sm text-slate-700">{hub.manager_name}</span>
                              </div>
                            </td>
                            <td className="px-4 py-4 align-middle">
                              <div className="space-y-1">
                                <div className="flex items-center gap-1.5 text-sm text-slate-600">
                                  <Phone className="w-3.5 h-3.5 text-slate-400" />{hub.manager_mobile}
                                </div>
                                {hub.manager_email && (
                                  <div className="flex items-center gap-1.5 text-sm text-slate-500">
                                    <Mail className="w-3.5 h-3.5 text-slate-400" />{hub.manager_email}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-4 align-middle">
                              {hub.coordinates ? (
                                <div className="flex items-center gap-1.5 text-xs text-slate-500 font-mono">
                                  <MapPin className="w-3.5 h-3.5 text-emerald-500" />
                                  {hub.coordinates.lat.toFixed(4)}, {hub.coordinates.lng.toFixed(4)}
                                </div>
                              ) : <span className="text-xs text-slate-400">Not set</span>}
                            </td>
                            <td className="px-4 py-4 align-middle">
                              {hub.is_active ? (
                                <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle className="w-3 h-3 mr-1" /> Active</Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" /> Inactive</Badge>
                              )}
                            </td>
                            <td className="px-4 py-4 align-middle">
                              <div className="flex gap-1.5">
                                <Button size="sm" variant="outline" onClick={() => navigate(`/pharmeasy/hub-ops/${hub._id}/layout`)}
                                  data-testid={`layout-btn-${hub._id}`} title="Hub Layout">
                                  <LayoutGrid className="w-4 h-4 mr-1" /> Layout
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => openEditHub(hub)} data-testid={`edit-hub-${hub._id}`}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Hub Selection Dialog for New Inward */}
      <Dialog open={showHubSelectDialog} onOpenChange={setShowHubSelectDialog}>
        <DialogContent className="max-w-md" aria-describedby="hub-select-desc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-600" />
              Select Hub for Inward
            </DialogTitle>
          </DialogHeader>
          <div className="py-4" id="hub-select-desc">
            <Label>Select Hub Location *</Label>
            <Select value={selectedHubId} onValueChange={setSelectedHubId}>
              <SelectTrigger className="mt-2" data-testid="hub-select-trigger">
                <SelectValue placeholder="Choose a hub..." />
              </SelectTrigger>
              <SelectContent>
                {hubs.map((hub) => (
                  <SelectItem key={hub._id} value={hub._id}>
                    <div className="flex flex-col">
                      <span className="font-semibold">{hub.hub_name}</span>
                      <span className="text-xs text-slate-500">{hub.city}, {hub.state}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-2">
              The inward wizard will open for the selected hub.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHubSelectDialog(false)}>Cancel</Button>
            <Button onClick={proceedToInward} style={{ backgroundColor: "#1B8C3D" }} data-testid="proceed-inward-btn">
              Proceed <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Hub Dialog */}
      <Dialog open={showHubDialog} onOpenChange={setShowHubDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" aria-describedby="wizard-desc">
          <DialogHeader>
            <DialogTitle>{editingHub ? "Edit Hub" : "Add New Hub"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleHubSubmit} className="space-y-5 py-2">
            <div>
              <Label htmlFor="hub_name">Hub Name *</Label>
              <Input id="hub_name" value={formData.hub_name} onChange={(e) => setFormData({ ...formData, hub_name: e.target.value })}
                placeholder="e.g. Mumbai Central Hub" required className="mt-1" data-testid="hub-name-input" />
            </div>
            <div>
              <Label htmlFor="hub_address">Address *</Label>
              <div className="flex gap-2 mt-1">
                <Input id="hub_address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Enter full address, e.g. Kukatpally Housing Board Colony, Hyderabad" required className="flex-1"
                  data-testid="hub-address-input" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); locateAddress(); } }} />
                <Button type="button" onClick={locateAddress} disabled={locating} variant="outline" className="shrink-0" data-testid="hub-locate-btn">
                  <Crosshair className={`w-4 h-4 mr-1.5 ${locating ? "animate-spin" : ""}`} />
                  {locating ? "Locating..." : "Locate on Map"}
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-1">Type address and click "Locate on Map", or click directly on the map. Drag marker to adjust.</p>
            </div>
            <div ref={mapContainerRef} className="w-full h-64 rounded-lg border border-slate-200 bg-slate-100" data-testid="hub-map-container" />
            {formData.coordinates && (
              <div className="flex items-center gap-2 text-xs text-slate-500 font-mono bg-slate-50 px-3 py-2 rounded-md">
                <MapPin className="w-3.5 h-3.5 text-emerald-600" />
                Lat: {formData.coordinates.lat.toFixed(6)}, Lng: {formData.coordinates.lng.toFixed(6)}
              </div>
            )}
            <div className="grid grid-cols-3 gap-4">
              <div><Label htmlFor="city">City</Label><Input id="city" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} placeholder="City" className="mt-1" data-testid="hub-city-input" /></div>
              <div><Label htmlFor="state">State</Label><Input id="state" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} placeholder="State" className="mt-1" data-testid="hub-state-input" /></div>
              <div><Label htmlFor="pincode">Pincode</Label><Input id="pincode" value={formData.pincode} onChange={(e) => setFormData({ ...formData, pincode: e.target.value })} placeholder="Pincode" className="mt-1" data-testid="hub-pincode-input" /></div>
            </div>
            <div className="border-t pt-4">
              <p className="text-sm font-semibold text-slate-700 mb-1">Manager Details</p>
              {!editingHub && <p className="text-xs text-slate-400 mb-3">A hub_manager account will be created with this email & password</p>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label htmlFor="manager_name">Manager Name *</Label><Input id="manager_name" value={formData.manager_name} onChange={(e) => setFormData({ ...formData, manager_name: e.target.value })} placeholder="Full Name" required className="mt-1" data-testid="hub-manager-name-input" /></div>
                <div>
                  <Label htmlFor="manager_email">Manager Email *</Label>
                  <Input id="manager_email" type="email" value={formData.manager_email} onChange={(e) => setFormData({ ...formData, manager_email: e.target.value })}
                    placeholder="email@example.com" required={!editingHub} disabled={!!editingHub}
                    className={`mt-1 ${editingHub ? 'bg-slate-100 cursor-not-allowed' : ''}`} data-testid="hub-manager-email-input" />
                  {editingHub && <p className="text-xs text-slate-500 mt-1">Email cannot be changed after creation</p>}
                </div>
                <div><Label htmlFor="manager_mobile">Manager Mobile *</Label><Input id="manager_mobile" value={formData.manager_mobile} onChange={(e) => setFormData({ ...formData, manager_mobile: e.target.value })} placeholder="+91 XXXXXXXXXX" required className="mt-1" data-testid="hub-manager-mobile-input" /></div>
                {!editingHub && (
                  <div><Label htmlFor="manager_password">Manager Password *</Label><Input id="manager_password" type="password" value={formData.manager_password} onChange={(e) => setFormData({ ...formData, manager_password: e.target.value })} placeholder="Min 6 characters" required className="mt-1" data-testid="hub-manager-password-input" /></div>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="notes">Additional Notes</Label>
              <textarea id="notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Operating hours, special instructions, etc." rows={3}
                className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="hub-notes-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowHubDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} style={{ backgroundColor: "#1B8C3D" }} data-testid="hub-save-btn">
                {saving ? "Saving..." : editingHub ? "Update Hub" : "Create Hub"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Inward Wizard Modal */}
      <Dialog open={showWizard} onOpenChange={setShowWizard}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-emerald-600" />
              New Inward - Step {wizardStep} of {totalWizardSteps}
              {wizardHub && <Badge variant="outline" className="ml-2">{wizardHub.hub_name}</Badge>}
            </DialogTitle>
          </DialogHeader>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-6 px-4">
            {Array.from({ length: totalWizardSteps }, (_, i) => i + 1).map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold
                  ${wizardStep >= step ? "bg-emerald-600 text-white" : "bg-slate-200 text-slate-500"}`}>
                  {wizardStep > step ? <Check className="w-4 h-4" /> : step}
                </div>
                {step < totalWizardSteps && (
                  <div className={`w-12 md:w-24 h-1 mx-1 ${wizardStep > step ? "bg-emerald-600" : "bg-slate-200"}`} />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Select Shipments */}
          {wizardStep === 1 && (
            <div className="space-y-4">
              <Tabs value={inputMode} onValueChange={setInputMode}>
                <TabsList className="grid w-full max-w-sm grid-cols-2">
                  <TabsTrigger value="filter"><Filter className="w-4 h-4 mr-1" /> Filter</TabsTrigger>
                  <TabsTrigger value="bulk"><Upload className="w-4 h-4 mr-1" /> Bulk Upload</TabsTrigger>
                </TabsList>

                <TabsContent value="filter" className="space-y-4 mt-4">
                  {/* Row 1: In Last Fetch + Expected Delivery */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-semibold text-teal-700">In Last Fetch</Label>
                      <div className="mt-2 space-y-2">
                        {filterOptions.presentInLastFetch?.map((item) => {
                          const val = typeof item === "string" ? item : item.value;
                          const cnt = typeof item === "string" ? null : item.count;
                          return (
                            <div key={val} className="flex items-center gap-2">
                              <Checkbox id={`pf-${val}`} checked={selectedFilters.presentInLastFetch?.includes(val)} onCheckedChange={(checked) => handleFilterChange("presentInLastFetch", val, checked)} />
                              <label htmlFor={`pf-${val}`} className="text-sm">{val}{cnt != null && <span className="text-slate-400 ml-1">({cnt})</span>}</label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <EddPicker
                        eddGroups={filterOptions.edds || []}
                        selectedEdds={selectedFilters.edds || []}
                        onEddChange={(timestamp, checked) => handleFilterChange("edds", timestamp, checked)}
                      />
                    </div>
                  </div>

                  {/* Row 2: Pincode + City */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-semibold">Pincode</Label>
                      <div className="mt-2 space-y-2 max-h-32 overflow-y-auto">
                        {filterOptions.pincodes?.map((item) => {
                          const val = typeof item === "string" ? item : item.value;
                          const cnt = typeof item === "string" ? null : item.count;
                          return (
                            <div key={val} className="flex items-center gap-2">
                              <Checkbox id={`pincode-${val}`} checked={selectedFilters.pincodes?.includes(val)} onCheckedChange={(checked) => handleFilterChange("pincodes", val, checked)} />
                              <label htmlFor={`pincode-${val}`} className="text-sm font-mono">{val}{cnt != null && <span className="text-slate-400 font-sans ml-1">({cnt})</span>}</label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">City</Label>
                      <div className="mt-2 space-y-2 max-h-32 overflow-y-auto">
                        {filterOptions.cities?.map((item) => {
                          const val = typeof item === "string" ? item : item.value;
                          const cnt = typeof item === "string" ? null : item.count;
                          return (
                            <div key={val} className="flex items-center gap-2">
                              <Checkbox id={`city-${val}`} checked={selectedFilters.cities?.includes(val)} onCheckedChange={(checked) => handleFilterChange("cities", val, checked)} />
                              <label htmlFor={`city-${val}`} className="text-sm">{val}{cnt != null && <span className="text-slate-400 ml-1">({cnt})</span>}</label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Row 3: Payment Type + Shipment Type */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-semibold">Payment Type</Label>
                      <div className="mt-2 space-y-2">
                        {filterOptions.paymentTypes?.map((item) => {
                          const val = typeof item === "string" ? item : item.value;
                          const cnt = typeof item === "string" ? null : item.count;
                          return (
                            <div key={val} className="flex items-center gap-2">
                              <Checkbox id={`payment-${val}`} checked={selectedFilters.paymentTypes?.includes(val)} onCheckedChange={(checked) => handleFilterChange("paymentTypes", val, checked)} />
                              <label htmlFor={`payment-${val}`} className="text-sm">{val}{cnt != null && <span className="text-slate-400 ml-1">({cnt})</span>}</label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">Shipment Type</Label>
                      <div className="mt-2 space-y-2">
                        {filterOptions.shipmentTypes?.map((item) => {
                          const val = typeof item === "string" ? item : item.value;
                          const cnt = typeof item === "string" ? null : item.count;
                          return (
                            <div key={val} className="flex items-center gap-2">
                              <Checkbox id={`shipment-${val}`} checked={selectedFilters.shipmentTypes?.includes(val)} onCheckedChange={(checked) => handleFilterChange("shipmentTypes", val, checked)} />
                              <label htmlFor={`shipment-${val}`} className="text-sm">{val}{cnt != null && <span className="text-slate-400 ml-1">({cnt})</span>}</label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Row 4: OFD Count + Unattempted Count */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-semibold text-blue-700">OFD Count</Label>
                      <div className="mt-2 space-y-2 max-h-32 overflow-y-auto">
                        {filterOptions.ofdCounts?.map((item) => {
                          const val = typeof item === "string" || typeof item === "number" ? String(item) : String(item.value);
                          const cnt = typeof item === "object" && item !== null ? item.count : null;
                          return (
                            <div key={val} className="flex items-center gap-2">
                              <Checkbox id={`ofd-${val}`} checked={selectedFilters.ofdCounts?.includes(val)} onCheckedChange={(checked) => handleFilterChange("ofdCounts", val, checked)} />
                              <label htmlFor={`ofd-${val}`} className="text-sm">{val}{cnt != null && <span className="text-slate-400 ml-1">({cnt})</span>}</label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-semibold text-red-700">Unattempted Count</Label>
                      <div className="mt-2 space-y-2 max-h-32 overflow-y-auto">
                        {filterOptions.unattemptedCounts?.map((item) => {
                          const val = typeof item === "string" || typeof item === "number" ? String(item) : String(item.value);
                          const cnt = typeof item === "object" && item !== null ? item.count : null;
                          return (
                            <div key={val} className="flex items-center gap-2">
                              <Checkbox id={`unattempt-${val}`} checked={selectedFilters.unattemptedCounts?.includes(val)} onCheckedChange={(checked) => handleFilterChange("unattemptedCounts", val, checked)} />
                              <label htmlFor={`unattempt-${val}`} className="text-sm">{val}{cnt != null && <span className="text-slate-400 ml-1">({cnt})</span>}</label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Row 5: Latest Event Status */}
                  <div>
                    <Label className="text-sm font-semibold text-teal-700">Latest Event Status</Label>
                    <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                      {filterOptions.receivedAtHub?.map((item) => {
                        const val = typeof item === "string" ? item : String(item.value);
                        const cnt = typeof item === "object" && item !== null ? item.count : null;
                        return (
                          <div key={val} className="flex items-center gap-2">
                            <Checkbox id={`hub-${val}`} checked={selectedFilters.receivedAtHub?.includes(val)} onCheckedChange={(checked) => handleFilterChange("receivedAtHub", val, checked)} />
                            <label htmlFor={`hub-${val}`} className="text-sm">
                              {val}
                              {cnt != null && <span className="text-slate-400 ml-1">({cnt})</span>}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="bg-emerald-50 p-4 rounded-lg">
                    <p className="font-semibold text-emerald-800">
                      Filtered Count: <span className="text-xl">{filteredCount}</span> shipments
                    </p>
                    <p className="text-xs text-emerald-600 mt-1">
                      {Object.values(selectedFilters).every(arr => arr.length === 0) 
                        ? "No filters applied — showing all unassigned shipments" 
                        : "Filters applied — count updates automatically"}
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="bulk" className="space-y-4 mt-4">
                  <div>
                    <Label>ID Type</Label>
                    <Select value={bulkIdType} onValueChange={setBulkIdType}>
                      <SelectTrigger className="w-48 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="shipmentId">Shipment ID</SelectItem>
                        <SelectItem value="packageId">Package ID</SelectItem>
                        <SelectItem value="lbn">LBN</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Paste IDs (one per line or comma-separated)</Label>
                    <textarea
                      value={bulkText}
                      onChange={(e) => {
                        setBulkText(e.target.value);
                        // Parse and validate IDs after a short delay
                        const ids = e.target.value.split(/[\n,\s]+/).map(id => id.trim()).filter(id => id);
                        if (ids.length > 0 && wizardHubId) {
                          validateBulkIds(wizardHubId, ids, bulkIdType);
                        } else {
                          setBulkValidatedCount(0);
                          setBulkShipments([]);
                        }
                      }}
                      rows={6}
                      className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                      placeholder="ID1&#10;ID2&#10;ID3..."
                    />
                  </div>
                  {bulkText.trim() && (
                    <div className="bg-emerald-50 p-4 rounded-lg">
                      <p className="font-semibold text-emerald-800">
                        Validated Count: <span className="text-xl">{bulkValidatedCount}</span> shipments found
                      </p>
                      <p className="text-xs text-emerald-600 mt-1">
                        {bulkText.split(/[\n,\s]+/).filter(id => id.trim()).length} IDs entered, {bulkValidatedCount} matched in system
                      </p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          )}

          {/* Step 2: Method of Planning */}
          {wizardStep === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-slate-800 mb-1">Choose Planning Method</h3>
                <p className="text-sm text-slate-500">How would you like to plan delivery trips?</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
                {/* Option 1: Map Route Optimization */}
                <div
                  onClick={() => setPlanningMethod("route")}
                  data-testid="method-route"
                  className={`cursor-pointer rounded-xl border-2 p-6 transition-all hover:shadow-md ${
                    planningMethod === "route"
                      ? "border-emerald-500 bg-emerald-50 shadow-md"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex flex-col items-center text-center gap-3">
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                      planningMethod === "route" ? "bg-emerald-100" : "bg-slate-100"
                    }`}>
                      <Route className={`w-7 h-7 ${planningMethod === "route" ? "text-emerald-600" : "text-slate-500"}`} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800">Map Route Optimization</h4>
                      <p className="text-sm text-slate-500 mt-1">
                        Auto-clusters shipments by proximity using Google Maps & OR-Tools.
                        Set rider count and capacity, trips are created with optimized delivery routes.
                      </p>
                    </div>
                    {planningMethod === "route" && (
                      <Badge className="bg-emerald-600 text-white">Selected</Badge>
                    )}
                  </div>
                </div>

                {/* Option 2: Pincode Trip Planning */}
                <div
                  onClick={() => setPlanningMethod("pincode")}
                  data-testid="method-pincode"
                  className={`cursor-pointer rounded-xl border-2 p-6 transition-all hover:shadow-md ${
                    planningMethod === "pincode"
                      ? "border-blue-500 bg-blue-50 shadow-md"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex flex-col items-center text-center gap-3">
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                      planningMethod === "pincode" ? "bg-blue-100" : "bg-slate-100"
                    }`}>
                      <Grid3X3 className={`w-7 h-7 ${planningMethod === "pincode" ? "text-blue-600" : "text-slate-500"}`} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800">Pincode Trip Planning</h4>
                      <p className="text-sm text-slate-500 mt-1">
                        Manually group shipments by pincode. Select pincodes, assign riders, and split shipment counts as needed.
                        Route optimization runs after trip creation for delivery sequence.
                      </p>
                    </div>
                    {planningMethod === "pincode" && (
                      <Badge className="bg-blue-600 text-white">Selected</Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Select Riders (Route method) OR Pincode Planner (Pincode method) */}
          {wizardStep === 3 && planningMethod === "route" && (() => {
            // Use bulk count if in bulk mode, otherwise use filter count
            const effectiveCount = inputMode === "bulk" ? bulkValidatedCount : filteredCount;
            const effectiveShipments = inputMode === "bulk" ? bulkShipments : [];
            
            return (
            <div className="space-y-6">
              {/* Selected Shipments Summary with Export */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-emerald-600" />
                    <span className="text-sm text-emerald-800">
                      <strong>Selected Shipments:</strong> <span className="text-xl font-bold">{effectiveCount}</span> parcels
                      {inputMode === "bulk" && <span className="text-xs ml-2">(via Bulk Upload)</span>}
                    </span>
                  </div>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="text-emerald-700 border-emerald-300 hover:bg-emerald-100"
                    onClick={async () => {
                      try {
                        toast.info("Exporting shipments...");
                        
                        let exportData = [];
                        if (inputMode === "bulk" && bulkShipments.length > 0) {
                          // Use already fetched bulk shipments
                          exportData = bulkShipments.map(s => ({
                            "Shipment ID": s.shipmentId || "",
                            "Type": s.type || s.shipmentType || "",
                            "Reference ID": s.referenceId || "",
                            "LBN": s.lbn || "",
                            "Package ID": s.packageId || "",
                            "Pharmeasy Status": s.bolt_status_raw || "",
                            "Edgistify Status": s.bolt_status || "",
                            "Address": s.dropAddress || s.destAddress || "",
                            "Payment": s.paymentType || "",
                            "Pincode": s.dropPincode || "",
                            "City": s.dropCity || "",
                            "EDD": s.committedExpectedDeliveryDate || s.edd || "",
                          }));
                        } else {
                          // Fetch filtered shipments
                          const params = new URLSearchParams();
                          if (selectedFilters.paymentTypes?.length) params.append("paymentTypes", selectedFilters.paymentTypes.join(","));
                          if (selectedFilters.shipmentTypes?.length) params.append("shipmentTypes", selectedFilters.shipmentTypes.join(","));
                          if (selectedFilters.cities?.length) params.append("cities", selectedFilters.cities.join(","));
                          if (selectedFilters.pincodes?.length) params.append("pincodes", selectedFilters.pincodes.join(","));
                          if (selectedFilters.edds?.length) params.append("edds", selectedFilters.edds.join(","));
                          if (selectedFilters.ofdCounts?.length) params.append("ofdCounts", selectedFilters.ofdCounts.join(","));
                          if (selectedFilters.unattemptedCounts?.length) params.append("unattemptedCounts", selectedFilters.unattemptedCounts.join(","));
                          if (selectedFilters.receivedAtHub?.length) params.append("receivedAtHub", selectedFilters.receivedAtHub.join(","));
                          
                          const res = await axios.get(`${API}/inward/${wizardHubId}/export-filtered?${params.toString()}`);
                          exportData = res.data.data;
                        }
                        
                        // Convert to CSV
                        const headers = ["Shipment ID", "Type", "Reference ID", "LBN", "Package ID", "Pharmeasy Status", "Edgistify Status", "Address", "Payment", "Pincode", "City", "EDD"];
                        const csvRows = [headers.join(",")];
                        exportData.forEach(row => {
                          const values = headers.map(h => `"${(row[h] || "").toString().replace(/"/g, '""')}"`);
                          csvRows.push(values.join(","));
                        });
                        
                        const csvContent = csvRows.join("\n");
                        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement("a");
                        link.href = url;
                        link.download = `selected_shipments_${new Date().toISOString().slice(0,10)}.csv`;
                        link.click();
                        URL.revokeObjectURL(url);
                        toast.success(`Exported ${exportData.length} shipments`);
                      } catch (e) {
                        toast.error("Failed to export");
                      }
                    }}
                  >
                    <Download className="w-4 h-4 mr-1" /> Export CSV
                  </Button>
                </div>
              </div>

              {/* Address Data Status Panel */}
              <div className="border rounded-lg p-4" data-testid="address-status-panel">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-indigo-600" />
                    <span className="text-sm font-semibold text-slate-700">Address Data Status</span>
                  </div>
                  {addressStatusLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                </div>
                {addressStatus && !addressStatusLoading && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-slate-600">Total: <strong>{addressStatus.total}</strong></span>
                      <span className="text-emerald-700">With Address: <strong>{addressStatus.with_address}</strong></span>
                      {addressStatus.without_address > 0 && (
                        <span className="text-red-600">Missing: <strong>{addressStatus.without_address}</strong></span>
                      )}
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${addressStatus.without_address > 0 ? 'bg-red-500' : 'bg-emerald-500'}`}
                        style={{ width: `${addressStatus.total > 0 ? (addressStatus.with_address / addressStatus.total * 100) : 0}%` }}
                      />
                    </div>
                    {addressStatus.without_address > 0 ? (
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-red-700">
                          <AlertTriangle className="w-3 h-3 inline mr-1" />
                          {addressStatus.without_address} shipment{addressStatus.without_address > 1 ? 's' : ''} missing address data. Fetch details from Bolt API to fix.
                        </p>
                        <Button
                          size="sm"
                          onClick={fixMissingAddresses}
                          disabled={fixingAddresses}
                          data-testid="fix-addresses-btn"
                          className="bg-red-600 hover:bg-red-700 text-white"
                        >
                          {fixingAddresses ? (
                            <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Fetching Details...</>
                          ) : (
                            <><RefreshCw className="w-3 h-3 mr-1" /> Fix {addressStatus.without_address} Missing</>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <p className="text-xs text-emerald-700 mt-1">
                        <Check className="w-3 h-3 inline mr-1" />
                        All shipments have address data.
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Coordinate Status Panel */}
              <div className="border rounded-lg p-4" data-testid="coord-status-panel">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-semibold text-slate-700">Google Coordinates Status</span>
                  </div>
                  {coordStatusLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                </div>
                {coordStatus && !coordStatusLoading && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-slate-600">
                        Total: <strong>{coordStatus.total}</strong>
                      </span>
                      <span className="text-emerald-700">
                        With Coords: <strong>{coordStatus.with_coordinates}</strong>
                      </span>
                      {coordStatus.without_coordinates > 0 && (
                        <span className="text-red-600">
                          Missing: <strong>{coordStatus.without_coordinates}</strong>
                        </span>
                      )}
                    </div>
                    {/* Progress bar */}
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${coordStatus.without_coordinates > 0 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                        style={{ width: `${coordStatus.total > 0 ? (coordStatus.with_coordinates / coordStatus.total * 100) : 0}%` }}
                      />
                    </div>
                    {coordStatus.without_coordinates > 0 ? (
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-amber-700">
                          <AlertTriangle className="w-3 h-3 inline mr-1" />
                          {coordStatus.without_coordinates} shipment{coordStatus.without_coordinates > 1 ? 's' : ''} missing coordinates. Fix required for accurate route optimization.
                        </p>
                        <Button
                          size="sm"
                          onClick={fixMissingCoordinates}
                          disabled={fixingCoords}
                          data-testid="fix-coordinates-btn"
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                        >
                          {fixingCoords ? (
                            <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Fixing...</>
                          ) : (
                            <><MapPin className="w-3 h-3 mr-1" /> Fix {coordStatus.without_coordinates} Missing</>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <p className="text-xs text-emerald-700 mt-1">
                        <Check className="w-3 h-3 inline mr-1" />
                        All shipments have valid coordinates. Ready for route optimization.
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Max Shipments Setting */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="riderCount" className="text-sm font-semibold">Number of Riders to Select</Label>
                  <Input id="riderCount" type="number" min="1" max="20" value={riderCount} onChange={(e) => setRiderCount(e.target.value)} className="mt-1" />
                  {/* Minimum riders calculation */}
                  {effectiveCount > 0 && parseInt(capacityPerRider) > 0 && (
                    <p className="text-xs mt-1 text-amber-600">
                      <AlertTriangle className="w-3 h-3 inline mr-1" />
                      Minimum riders needed: <strong>{Math.ceil(effectiveCount / parseInt(capacityPerRider))}</strong> 
                      {" "}(based on {effectiveCount} parcels ÷ {capacityPerRider} max per trip)
                    </p>
                  )}
                  {/* Location validation */}
                  {parseInt(riderCount) > locations.length && locations.length > 0 && (
                    <p className="text-xs mt-1 text-red-600">
                      <AlertTriangle className="w-3 h-3 inline mr-1" />
                      Insufficient locations! Need {riderCount}, but only {locations.length} available in hub layout.
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="capacity" className="text-sm font-semibold">Max Shipments per Trip</Label>
                  <Input id="capacity" type="number" min="1" max="100" value={capacityPerRider} onChange={(e) => setCapacityPerRider(e.target.value)} className="mt-1" />
                  <p className="text-xs text-slate-500 mt-1">Default: {defaultCapacity}</p>
                </div>
              </div>

              {/* Return to Hub Toggle */}
              <div className="flex items-center justify-between border rounded-lg p-4" data-testid="return-to-hub-toggle">
                <div>
                  <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <Home className="w-4 h-4 text-blue-600" />
                    Return to Hub
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {returnToHub
                      ? "Route will be optimized as a round trip (hub → deliveries → hub)"
                      : "Route ends at the last delivery point (hub → deliveries → end)"}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={returnToHub}
                  onClick={() => setReturnToHub(prev => !prev)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${returnToHub ? "bg-emerald-500" : "bg-slate-300"}`}
                  data-testid="return-to-hub-switch"
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${returnToHub ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>

              {/* Available Riders List */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold">Select Riders ({selectedRiders.length} of {riderCount} selected)</Label>
                  {selectedRiders.length < parseInt(riderCount) && (
                    <span className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Select {parseInt(riderCount) - selectedRiders.length} more rider{parseInt(riderCount) - selectedRiders.length > 1 ? 's' : ''} to proceed
                    </span>
                  )}
                  {selectedRiders.length >= parseInt(riderCount) && (
                    <span className="text-xs text-emerald-600 flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Rider selection complete
                    </span>
                  )}
                </div>
                <div className="border rounded-lg">
                  {/* Rider Search */}
                  <div className="px-3 py-2 border-b bg-slate-50">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search rider by name..."
                        value={riderSearch}
                        onChange={e => setRiderSearch(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-sm border rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        data-testid="rider-search-input"
                      />
                    </div>
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                  {availableRiders.length === 0 ? (
                    <div className="p-4 text-center text-slate-500">
                      <Users className="w-8 h-8 mx-auto mb-2 text-slate-300" />

                      <p>No active riders found</p>
                      <p className="text-xs">Sync riders from the Riders page</p>
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-3 text-left w-10">
                            <input 
                              type="checkbox" 
                              checked={selectedRiders.length === Math.min(parseInt(riderCount) || 0, availableRiders.length)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedRiders(availableRiders.slice(0, parseInt(riderCount) || 3).map(r => r._id));
                                } else {
                                  setSelectedRiders([]);
                                }
                              }}
                              className="rounded"
                            />
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Name</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Mobile</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Hub</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Capacity</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {availableRiders
                          .filter(rider => !riderSearch || rider.name?.toLowerCase().includes(riderSearch.toLowerCase()) || rider.mobile?.includes(riderSearch))
                          .map((rider) => (
                          <tr 
                            key={rider._id} 
                            className={`hover:bg-slate-50 cursor-pointer ${selectedRiders.includes(rider._id) ? 'bg-emerald-50' : ''}`}
                            onClick={() => {
                              if (selectedRiders.includes(rider._id)) {
                                setSelectedRiders(selectedRiders.filter(id => id !== rider._id));
                              } else if (selectedRiders.length < parseInt(riderCount)) {
                                setSelectedRiders([...selectedRiders, rider._id]);
                              } else {
                                toast.warning(`Maximum ${riderCount} riders can be selected`);
                              }
                            }}
                          >
                            <td className="px-4 py-3 align-middle">
                              <input 
                                type="checkbox" 
                                checked={selectedRiders.includes(rider._id)}
                                readOnly
                                className="rounded"
                              />
                            </td>
                            <td className="px-4 py-3 align-middle">
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center text-orange-700 font-semibold text-xs">
                                  {rider.name?.charAt(0)?.toUpperCase() || "?"}
                                </div>
                                <span className="font-medium">{rider.name || "—"}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 align-middle font-mono text-slate-600">{rider.mobile || "—"}</td>
                            <td className="px-4 py-3 align-middle text-slate-600">{rider.assigned_asset_name || "—"}</td>
                            <td className="px-4 py-3 align-middle text-center">
                              <Badge variant="outline" className="bg-blue-50 text-blue-700">
                                {rider.max_capacity || defaultCapacity}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 align-middle">
                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                {rider.bolt_status || "ACTIVE"}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  </div>
                </div>
              </div>

              {/* Capacity Summary */}
              <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                <p className="text-sm text-emerald-800">
                  <strong>{selectedRiders.length || riderCount}</strong> riders × <strong>{capacityPerRider}</strong> max shipments = 
                  Up to <strong>{(parseInt(selectedRiders.length || riderCount)) * parseInt(capacityPerRider)}</strong> total capacity
                </p>
              </div>
            </div>
            );
          })()}

          {/* Step 3: Pincode Trip Planner (Pincode method) */}
          {wizardStep === 3 && planningMethod === "pincode" && (
            <div className="space-y-4">
              {/* Address Data Status Panel for Pincode */}
              <div className="border rounded-lg p-4" data-testid="address-status-panel-pincode">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-indigo-600" />
                    <span className="text-sm font-semibold text-slate-700">Address Data Status</span>
                  </div>
                  {addressStatusLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                </div>
                {addressStatus && !addressStatusLoading && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-slate-600">Total: <strong>{addressStatus.total}</strong></span>
                      <span className="text-emerald-700">With Address: <strong>{addressStatus.with_address}</strong></span>
                      {addressStatus.without_address > 0 && (
                        <span className="text-red-600">Missing: <strong>{addressStatus.without_address}</strong></span>
                      )}
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${addressStatus.without_address > 0 ? 'bg-red-500' : 'bg-emerald-500'}`}
                        style={{ width: `${addressStatus.total > 0 ? (addressStatus.with_address / addressStatus.total * 100) : 0}%` }}
                      />
                    </div>
                    {addressStatus.without_address > 0 ? (
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-red-700">
                          <AlertTriangle className="w-3 h-3 inline mr-1" />
                          {addressStatus.without_address} shipment{addressStatus.without_address > 1 ? 's' : ''} missing address data. Fetch details from Bolt API to fix.
                        </p>
                        <Button
                          size="sm"
                          onClick={fixMissingAddresses}
                          disabled={fixingAddresses}
                          data-testid="fix-addresses-btn-pincode"
                          className="bg-red-600 hover:bg-red-700 text-white"
                        >
                          {fixingAddresses ? (
                            <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Fetching Details...</>
                          ) : (
                            <><RefreshCw className="w-3 h-3 mr-1" /> Fix {addressStatus.without_address} Missing</>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <p className="text-xs text-emerald-700 mt-1">
                        <Check className="w-3 h-3 inline mr-1" />
                        All shipments have address data.
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Coordinate Status Panel for Pincode */}
              <div className="border rounded-lg p-4" data-testid="coord-status-panel-pincode">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-semibold text-slate-700">Google Coordinates Status</span>
                  </div>
                  {coordStatusLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                </div>
                {coordStatus && !coordStatusLoading && (
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-slate-600">Total: <strong>{coordStatus.total}</strong></span>
                      <span className="text-emerald-700">With Coords: <strong>{coordStatus.with_coordinates}</strong></span>
                      {coordStatus.without_coordinates > 0 && (
                        <span className="text-red-600">Missing: <strong>{coordStatus.without_coordinates}</strong></span>
                      )}
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${coordStatus.without_coordinates > 0 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                        style={{ width: `${coordStatus.total > 0 ? (coordStatus.with_coordinates / coordStatus.total * 100) : 0}%` }}
                      />
                    </div>
                    {coordStatus.without_coordinates > 0 ? (
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-amber-700">
                          <AlertTriangle className="w-3 h-3 inline mr-1" />
                          {coordStatus.without_coordinates} shipment{coordStatus.without_coordinates > 1 ? 's' : ''} missing coordinates.
                        </p>
                        <Button
                          size="sm"
                          onClick={fixMissingCoordinates}
                          disabled={fixingCoords}
                          data-testid="fix-coordinates-btn-pincode"
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                        >
                          {fixingCoords ? (
                            <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Fixing...</>
                          ) : (
                            <><MapPin className="w-3 h-3 mr-1" /> Fix {coordStatus.without_coordinates} Missing</>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <p className="text-xs text-emerald-700 mt-1">
                        <Check className="w-3 h-3 inline mr-1" />
                        All shipments have valid coordinates.
                      </p>
                    )}
                  </div>
                )}
              </div>

              {pincodeSummaryLoading ? (
                <div className="text-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-blue-600" />
                  <p className="text-slate-600">Loading pincode summary...</p>
                </div>
              ) : pincodeSummary.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-600">No shipments found with the applied filters</p>
                </div>
              ) : (
                <PincodeTripPlanner
                  pincodeSummary={pincodeSummary}
                  availableRiders={availableRiders}
                  plannedTrips={pincodePlannedTrips}
                  setPlannedTrips={setPincodePlannedTrips}
                />
              )}
            </div>
          )}

          {/* Step 4: Create Trips (Route method) */}
          {wizardStep === 4 && planningMethod === "route" && (() => {
            const effectiveCount = inputMode === "bulk" ? bulkValidatedCount : filteredCount;
            return (
            <div className="space-y-4">
              {/* Selected Shipments Summary */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm text-emerald-800">
                    <strong>Selected Shipments:</strong> <span className="text-xl font-bold">{effectiveCount}</span> parcels to be assigned to <strong>{riderCount}</strong> riders
                    {inputMode === "bulk" && <span className="text-xs ml-2">(via Bulk Upload)</span>}
                  </span>
                </div>
              </div>

              {trips.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-600 mb-4">Create optimized trips for {riderCount} riders with {effectiveCount} shipments</p>
                  <div className="flex items-center justify-center gap-3">
                    <Button onClick={createTrips} disabled={creatingTrips} style={{ backgroundColor: "#1B8C3D" }}>
                      {creatingTrips ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      {creatingTrips ? "Creating..." : "Create & Optimize Trips"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={runAiPlanningAnalysis}
                      disabled={aiAnalyzing}
                      className="text-violet-600 hover:text-violet-700 hover:bg-violet-50 border-violet-200"
                      data-testid="ai-analyze-planning-btn"
                    >
                      <Sparkles className={`w-4 h-4 mr-1 ${aiAnalyzing ? "animate-spin" : ""}`} />
                      {aiAnalyzing ? "Analyzing..." : "AI Insights"}
                    </Button>
                  </div>
                  {/* AI Analysis Results */}
                  {aiAnalysis && !aiAnalysis.error && (
                    <div className="mt-4 text-left bg-gradient-to-br from-violet-50/50 to-white border border-violet-200 rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2 text-sm font-semibold text-violet-700">
                        <Sparkles className="w-4 h-4" /> AI Planning Analysis
                        <Badge variant="outline" className="text-[10px] text-violet-400 border-violet-200 ml-auto">{aiAnalysis.ai_model}</Badge>
                      </div>
                      <p className="text-sm text-slate-700">{aiAnalysis.summary}</p>
                      <div className="text-xs text-slate-600">
                        Recommended: <strong>{aiAnalysis.recommended_trip_count} trips</strong>, ~<strong>{aiAnalysis.estimated_riders_needed} riders</strong>
                      </div>
                      {aiAnalysis.grouping_suggestions?.length > 0 && (
                        <div>
                          <h5 className="text-xs font-semibold text-violet-700 mb-1">Suggested Groupings:</h5>
                          {aiAnalysis.grouping_suggestions.map((g, i) => (
                            <div key={i} className="text-xs bg-violet-50 rounded px-2 py-1 mb-1">
                              <strong>{g.trip_label}</strong>: {g.pincodes?.join(", ")} — {g.reason}
                            </div>
                          ))}
                        </div>
                      )}
                      {aiAnalysis.warnings?.length > 0 && (
                        <div>
                          <h5 className="text-xs font-semibold text-amber-700 mb-1">Warnings:</h5>
                          {aiAnalysis.warnings.map((w, i) => (
                            <div key={i} className="text-xs bg-amber-50 text-amber-700 rounded px-2 py-1 mb-1">{w}</div>
                          ))}
                        </div>
                      )}
                      {aiAnalysis.planning_insights?.length > 0 && (
                        <div>
                          <h5 className="text-xs font-semibold text-blue-700 mb-1">Insights:</h5>
                          {aiAnalysis.planning_insights.map((ins, i) => (
                            <div key={i} className="text-xs bg-blue-50 text-blue-700 rounded px-2 py-1 mb-1">{ins}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {aiAnalysis?.error && (
                    <div className="mt-3 p-3 bg-red-50 rounded text-xs text-red-700">
                      <AlertTriangle className="w-3 h-3 inline mr-1" />{aiAnalysis.error}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <h3 className="font-semibold text-slate-800">Created {trips.length} Trips for {riderCount} Riders</h3>
                  <div className="max-h-96 overflow-y-auto space-y-2">
                    {trips.map((trip) => (
                      <div key={trip.trip_number} className="bg-white border rounded-lg overflow-hidden">
                        <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                          onClick={() => setExpandedTrips(prev => ({...prev, [trip.trip_number]: !prev[trip.trip_number]}))}>
                          <div className="flex items-center gap-3">
                            {expandedTrips[trip.trip_number] ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                            <div>
                              <p className="font-semibold text-slate-800">Trip #{trip.trip_number}</p>
                              <p className="text-xs text-slate-500">Pincodes: {trip.pincodes_display || trip.pincodes?.join(", ") || trip.pincode}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-blue-600 font-semibold">{trip.shipment_count} shipments</span>
                            {trip.return_count > 0 && (
                              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                {trip.return_count} Return
                              </Badge>
                            )}
                            <span className="text-emerald-600">₹{trip.total_value?.toLocaleString()}</span>
                            <Badge variant="outline">{trip.cod_count} COD</Badge>
                          </div>
                        </div>
                        {expandedTrips[trip.trip_number] && (
                          <div className="border-t bg-slate-50 p-4">
                            <div className="max-h-48 overflow-y-auto">
                              <table className="w-full text-xs">
                                <thead className="bg-slate-100">
                                  <tr>
                                    <th className="px-3 py-2 text-left font-semibold text-slate-600">#</th>
                                    <th className="px-3 py-2 text-left font-semibold text-slate-600">Shipment ID</th>
                                    <th className="px-3 py-2 text-left font-semibold text-slate-600">Package ID</th>
                                    <th className="px-3 py-2 text-left font-semibold text-slate-600">Pincode</th>
                                    <th className="px-3 py-2 text-left font-semibold text-slate-600">Type</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200">
                                  {trip.shipments?.map((shipment, idx) => (
                                    <tr key={shipment._id || idx} className="hover:bg-white">
                                      <td className="px-3 py-2 text-slate-500">{shipment.delivery_sequence || idx + 1}</td>
                                      <td className="px-3 py-2 font-mono text-blue-600">{shipment.shipmentId}</td>
                                      <td className="px-3 py-2 font-mono text-slate-600">{shipment.packageId || "—"}</td>
                                      <td className="px-3 py-2">{shipment.dropPincode}</td>
                                      <td className="px-3 py-2">
                                        <Badge variant="outline" className={shipment.paymentType === "COD" ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700"}>
                                          {shipment.paymentType}
                                        </Badge>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            );
          })()}

          {/* Step 4: Create Trips - Pincode method (shows review of planned trips before creating) */}
          {wizardStep === 4 && planningMethod === "pincode" && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Grid3X3 className="w-5 h-5 text-blue-600" />
                  <span className="text-sm text-blue-800">
                    <strong>{pincodePlannedTrips.length} trips</strong> planned with <strong>{pincodePlannedTrips.reduce((s, t) => s + (t.shipment_count || 0), 0)}</strong> shipments.
                    Route optimization will be applied to each trip for delivery sequence.
                  </span>
                </div>
              </div>

              {trips.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-slate-600 mb-4">Click below to optimize routes and create trips</p>
                  <div className="flex items-center justify-center gap-3">
                    <Button onClick={createPincodeTrips} disabled={creatingTrips} style={{ backgroundColor: "#1B8C3D" }}>
                      {creatingTrips ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      {creatingTrips ? "Optimizing Routes..." : "Create & Optimize Trips"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={runAiPlanningAnalysis}
                      disabled={aiAnalyzing}
                      className="text-violet-600 hover:text-violet-700 hover:bg-violet-50 border-violet-200"
                      data-testid="ai-analyze-pincode-btn"
                    >
                      <Sparkles className={`w-4 h-4 mr-1 ${aiAnalyzing ? "animate-spin" : ""}`} />
                      {aiAnalyzing ? "Analyzing..." : "AI Insights"}
                    </Button>
                  </div>
                  {/* AI Analysis for Pincode planning */}
                  {aiAnalysis && !aiAnalysis.error && (
                    <div className="mt-4 text-left bg-gradient-to-br from-violet-50/50 to-white border border-violet-200 rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2 text-sm font-semibold text-violet-700">
                        <Sparkles className="w-4 h-4" /> AI Planning Analysis
                      </div>
                      <p className="text-sm text-slate-700">{aiAnalysis.summary}</p>
                      {aiAnalysis.grouping_suggestions?.length > 0 && (
                        <div>
                          <h5 className="text-xs font-semibold text-violet-700 mb-1">Suggested Groupings:</h5>
                          {aiAnalysis.grouping_suggestions.map((g, i) => (
                            <div key={i} className="text-xs bg-violet-50 rounded px-2 py-1 mb-1">
                              <strong>{g.trip_label}</strong>: {g.pincodes?.join(", ")} — {g.reason}
                            </div>
                          ))}
                        </div>
                      )}
                      {aiAnalysis.warnings?.length > 0 && aiAnalysis.warnings.map((w, i) => (
                        <div key={i} className="text-xs bg-amber-50 text-amber-700 rounded px-2 py-1">{w}</div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <h3 className="font-semibold text-slate-800">Created {trips.length} Optimized Trips</h3>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {trips.map((trip) => (
                      <div key={trip.trip_number} className="bg-white border rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-slate-800">Trip #{trip.trip_number}</p>
                            <p className="text-xs text-slate-500">Pincodes: {trip.pincodes_display || trip.pincodes?.join(", ")}</p>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-blue-600 font-semibold">{trip.shipment_count} shipments</span>
                            {tripRiderAssignments[trip.trip_number] && (
                              <Badge variant="outline" className="text-xs">
                                <User className="w-3 h-3 mr-1" />
                                {availableRiders.find(r => r._id === tripRiderAssignments[trip.trip_number])?.name || "Rider"}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 5: Assign & Save */}
          {wizardStep === 5 && (
            <div className="space-y-4">
              {assignments.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-lg text-slate-600 mb-4">
                    Assign {trips.length} trips to {locations.length} available locations
                  </p>
                  <Button onClick={assignTripsToLocations} disabled={assigning} style={{ backgroundColor: "#1B8C3D" }} className="px-8">
                    {assigning ? "Assigning..." : "Assign Trips to Locations"}
                  </Button>
                </div>
              ) : !saveResult ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-slate-800">Assignment Preview</h3>
                    {/* Duplicate rider warning */}
                    {(() => {
                      const usedRiders = Object.values(tripRiderAssignments).filter(Boolean);
                      const duplicates = usedRiders.filter((id, idx) => usedRiders.indexOf(id) !== idx);
                      if (duplicates.length > 0) {
                        const dupNames = [...new Set(duplicates)].map(id => {
                          const rider = availableRiders.find(r => r._id === id);
                          return rider?.name || id;
                        });
                        return (
                          <div className="flex items-center gap-2 text-red-600 text-sm">
                            <AlertTriangle className="w-4 h-4" />
                            <span>Duplicate rider: {dupNames.join(", ")}</span>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  <div className="max-h-[400px] overflow-y-auto space-y-3">
                    {assignments.map((assignment) => {
                      const assignedRiderId = tripRiderAssignments[assignment.trip_number];
                      const assignedRider = availableRiders.find(r => r._id === assignedRiderId);
                      // Get riders already assigned to other trips (for dropdown filtering)
                      const usedRiderIds = Object.entries(tripRiderAssignments)
                        .filter(([tripNum]) => parseInt(tripNum) !== assignment.trip_number)
                        .map(([, riderId]) => riderId);
                      
                      return (
                      <div key={assignment.trip_number} className="bg-white border rounded-lg overflow-hidden">
                        <div 
                          className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                          onClick={() => setExpandedAssignments(prev => ({...prev, [assignment.trip_number]: !prev[assignment.trip_number]}))}
                        >
                          <div className="flex items-center gap-3">
                            {expandedAssignments[assignment.trip_number] ? 
                              <ChevronUp className="w-4 h-4 text-slate-400" /> : 
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            }
                            <div>
                              <p className="font-semibold text-slate-800">Trip #{assignment.trip_number}</p>
                              <p className="text-xs text-slate-500">
                                Pincodes: <span className="font-medium text-slate-700">{assignment.pincodes_display || assignment.pincode || "—"}</span>
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {/* Rider Selection Dropdown */}
                            <div onClick={(e) => e.stopPropagation()}>
                              <Select
                                value={assignedRiderId || ""}
                                onValueChange={(value) => {
                                  setTripRiderAssignments(prev => ({
                                    ...prev,
                                    [assignment.trip_number]: value || null
                                  }));
                                }}
                              >
                                <SelectTrigger className="w-[180px] h-8 text-sm">
                                  <SelectValue placeholder="Select Rider">
                                    {assignedRider ? (
                                      <span className="flex items-center gap-2">
                                        <User className="w-3 h-3" />
                                        {assignedRider.name}
                                      </span>
                                    ) : "Select Rider"}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  {availableRiders.map((rider) => {
                                    const isUsedElsewhere = usedRiderIds.includes(rider._id);
                                    return (
                                      <SelectItem 
                                        key={rider._id} 
                                        value={rider._id}
                                        disabled={isUsedElsewhere}
                                        className={isUsedElsewhere ? "opacity-50" : ""}
                                      >
                                        <span className="flex items-center gap-2">
                                          <User className="w-3 h-3" />
                                          {rider.name}
                                          {isUsedElsewhere && <span className="text-xs text-slate-400">(assigned)</span>}
                                        </span>
                                      </SelectItem>
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                            </div>
                            <Badge className="bg-emerald-100 text-emerald-700">{assignment.location_name}</Badge>
                            <div className="text-right">
                              <p className="text-sm font-medium text-slate-700">{assignment.shipment_count} shipments</p>
                              <div className="flex gap-2 text-xs">
                                {assignment.forward_count > 0 && (
                                  <span className="text-blue-600">{assignment.forward_count} to scan</span>
                                )}
                                {assignment.return_count > 0 && (
                                  <span className="text-purple-600">{assignment.return_count} pickup</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Expandable shipment list */}
                        {expandedAssignments[assignment.trip_number] && assignment.sublocation_assignments && (
                          <div className="border-t bg-slate-50 p-4">
                            <div className="max-h-64 overflow-y-auto">
                              <table className="w-full text-xs">
                                <thead className="bg-slate-100 sticky top-0">
                                  <tr>
                                    <th className="px-3 py-2 text-left font-semibold text-slate-600">Seq</th>
                                    <th className="px-3 py-2 text-left font-semibold text-slate-600">Type</th>
                                    <th className="px-3 py-2 text-left font-semibold text-slate-600">Shipment ID</th>
                                    <th className="px-3 py-2 text-left font-semibold text-slate-600">Package ID</th>
                                    <th className="px-3 py-2 text-left font-semibold text-slate-600">Reference ID</th>
                                    <th className="px-3 py-2 text-left font-semibold text-slate-600">LBN</th>
                                    <th className="px-3 py-2 text-left font-semibold text-slate-600">Pincode</th>
                                    <th className="px-3 py-2 text-left font-semibold text-slate-600">Address</th>
                                    <th className="px-3 py-2 text-left font-semibold text-slate-600">Slot</th>
                                    <th className="px-3 py-2 text-center font-semibold text-slate-600">Map</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200">
                                  {assignment.sublocation_assignments.map((sub, subIdx) => {
                                    const mapLink = getMapLink(sub);
                                    const isReturn = sub.is_return || sub.shipment_type === "RETURN";
                                    return (
                                      <tr key={subIdx} className={`hover:bg-white ${isReturn ? "bg-purple-50" : ""}`}>
                                        <td className="px-3 py-2 text-slate-500">{sub.sequence}</td>
                                        <td className="px-3 py-2">
                                          <Badge className={`text-xs ${isReturn ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
                                            {sub.shipment_type || (isReturn ? "RETURN" : "FORWARD")}
                                          </Badge>
                                        </td>
                                        <td className="px-3 py-2 font-mono text-blue-600">{sub.shipment_code || "—"}</td>
                                        <td className="px-3 py-2 font-mono text-slate-600">{sub.package_id || "—"}</td>
                                        <td className="px-3 py-2 font-mono text-slate-600 max-w-[150px] truncate" title={sub.reference_id}>{sub.reference_id || "—"}</td>
                                        <td className="px-3 py-2 font-mono text-purple-600">{sub.lbn || "—"}</td>
                                        <td className="px-3 py-2 font-mono text-orange-600">{sub.drop_pincode || sub.dest_address?.pincode || "—"}</td>
                                        <td className="px-3 py-2 text-slate-600 max-w-[200px] truncate" title={formatAddress(sub.dest_address)}>
                                          {formatAddress(sub.dest_address)}
                                        </td>
                                        <td className="px-3 py-2">
                                          {isReturn ? (
                                            <Badge variant="outline" className="text-xs bg-purple-50 text-purple-600">PICKUP</Badge>
                                          ) : (
                                            <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700">{sub.sublocation_name}</Badge>
                                          )}
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                          {mapLink ? (
                                            <a href={mapLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center text-blue-500 hover:text-blue-700">
                                              <Navigation className="w-4 h-4" />
                                            </a>
                                          ) : (
                                            <span className="text-slate-300">—</span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                  <div className="bg-emerald-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-emerald-800">Ready to Save Inward Plan</p>
                        <p className="text-sm text-emerald-600">{assignments.length} trips • {assignments.reduce((sum, a) => sum + (a.shipment_count || 0), 0)} shipments</p>
                      </div>
                      {(() => {
                        const usedRiders = Object.values(tripRiderAssignments).filter(Boolean);
                        const hasDuplicates = usedRiders.length !== new Set(usedRiders).size;
                        return (
                          <Button 
                            onClick={saveWizardInward} 
                            disabled={wizardSaving || hasDuplicates} 
                            style={{ backgroundColor: hasDuplicates ? "#9CA3AF" : "#1B8C3D" }} 
                            className="px-6"
                            title={hasDuplicates ? "Fix duplicate rider assignments first" : ""}
                          >
                            {wizardSaving ? "Saving..." : "Save Inward Session"}
                          </Button>
                        );
                      })()}
                    </div>
                    <p className="text-xs text-emerald-600 mt-2">Shipments will be inwarded later through scanning</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-emerald-700 mb-2">Inward Session Created!</h3>
                  <p className="text-slate-600 mb-1">Session ID: <span className="font-mono font-semibold text-blue-600">{saveResult.session_code}</span></p>
                  <p className="text-sm text-slate-500 mb-4">{saveResult.total_shipments} shipments ready for scanning</p>
                  <div className="flex gap-3 justify-center">
                    <Button variant="outline" onClick={() => { setShowWizard(false); fetchAllInwardSessions(); }}>Close</Button>
                    <Button onClick={() => startWizardForHub(wizardHubId)} style={{ backgroundColor: "#1B8C3D" }}>Start Another Inward</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Footer Navigation */}
          {!saveResult && (
            <DialogFooter className="flex justify-between mt-6">
              <Button variant="outline" onClick={prevStep} disabled={wizardStep === 1}>
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>

              {/* Step 1 → Step 2 */}
              {wizardStep === 1 && (
                <Button onClick={nextStep} style={{ backgroundColor: "#1B8C3D" }}>
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}

              {/* Step 2 → Step 3: Must select a method */}
              {wizardStep === 2 && (
                <Button
                  onClick={() => {
                    if (planningMethod === "pincode") {
                      fetchPincodeSummary();
                    }
                    nextStep();
                  }}
                  disabled={!planningMethod}
                  style={{ backgroundColor: planningMethod ? "#1B8C3D" : "#9CA3AF" }}
                  data-testid="method-next-btn"
                >
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}

              {/* Step 3 (Route): Riders → Step 4 */}
              {wizardStep === 3 && planningMethod === "route" && (() => {
                const effectiveCount = inputMode === "bulk" ? bulkValidatedCount : filteredCount;
                const minRidersNeeded = effectiveCount > 0 && parseInt(capacityPerRider) > 0 
                  ? Math.ceil(effectiveCount / parseInt(capacityPerRider)) 
                  : 1;
                const ridersEntered = parseInt(riderCount) || 0;
                const hasEnoughRiders = ridersEntered >= minRidersNeeded;
                const hasEnoughLocations = locations.length === 0 || ridersEntered <= locations.length;
                const hasSelectedRiders = selectedRiders.length >= ridersEntered;
                const canProceed = hasEnoughRiders && hasEnoughLocations && hasSelectedRiders;
                
                let errorMessage = "";
                if (!hasEnoughRiders) {
                  errorMessage = `Need at least ${minRidersNeeded} riders`;
                } else if (!hasEnoughLocations) {
                  errorMessage = `Only ${locations.length} locations available in hub`;
                } else if (!hasSelectedRiders) {
                  errorMessage = `Select ${ridersEntered} riders from the list`;
                }
                
                return (
                  <Button 
                    onClick={nextStep} 
                    disabled={!canProceed}
                    style={{ backgroundColor: canProceed ? "#1B8C3D" : "#9CA3AF" }}
                    title={errorMessage}
                  >
                    Next <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                );
              })()}

              {/* Step 3 (Pincode): Planner → Step 4 */}
              {wizardStep === 3 && planningMethod === "pincode" && pincodePlannedTrips.length > 0 && (
                <Button onClick={nextStep} style={{ backgroundColor: "#1B8C3D" }} data-testid="pincode-planner-next-btn">
                  Review & Create Trips <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}

              {/* Step 4 (Route): Create trips → Step 5 */}
              {wizardStep === 4 && planningMethod === "route" && trips.length > 0 && (
                <Button onClick={nextStep} style={{ backgroundColor: "#1B8C3D" }}>
                  Next: Assign Locations <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Session Detail Modal */}
      <Dialog open={!!selectedSession} onOpenChange={(open) => !open && setSelectedSession(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-emerald-600" />
                Inward Session Details
              </DialogTitle>
              {selectedSession && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => exportSessionToCSV(selectedSession._id)}
                  className="flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </Button>
              )}
            </div>
          </DialogHeader>

          {sessionDetailLoading ? (
            <div className="text-center py-8">Loading session details...</div>
          ) : selectedSession && (
            <div className="space-y-6">
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Session ID</p>
                    <p className="font-mono text-sm font-semibold text-blue-600">{selectedSession.session_code || `INW${selectedSession._id?.slice(-4)}`}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Hub</p>
                    <p className="font-semibold text-slate-800">{selectedSession.hub_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Total Trips</p>
                    <p className="font-semibold text-2xl text-emerald-600">{selectedSession.total_trips}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Total Shipments</p>
                    <p className="font-semibold text-2xl text-blue-600">{selectedSession.total_shipments}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-200">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Created</p>
                    <p className="font-medium text-slate-700">{formatDate(selectedSession.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Created By</p>
                    <p className="font-medium text-slate-700">{selectedSession.created_by}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Status</p>
                    <Badge className={selectedSession.status === "COMPLETED" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                      {selectedSession.status}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Inwarded</p>
                    <p className="font-medium text-slate-700">{selectedSession.inwarded_count || 0} / {selectedSession.total_shipments}</p>
                  </div>
                </div>

                {selectedSession.status !== "COMPLETED" && (
                  <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Progress</p>
                      <div className="w-48 h-2 bg-slate-200 rounded-full mt-1">
                        <div className="h-full bg-emerald-500 rounded-full transition-all"
                          style={{ width: `${((selectedSession.inwarded_count || 0) / selectedSession.total_shipments * 100)}%` }} />
                      </div>
                    </div>
                    <Button onClick={() => startInwardScanning(selectedSession)} className="bg-emerald-600 hover:bg-emerald-700">
                      <ScanLine className="w-4 h-4 mr-2" /> Start Inward
                    </Button>
                  </div>
                )}
              </div>

              {/* Trip Assignments */}
              {selectedSession.assignments && selectedSession.assignments.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <Truck className="w-4 h-4" /> Trip Assignments ({selectedSession.assignments.length})
                  </h3>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {selectedSession.assignments.map((assignment, idx) => (
                      <div key={idx} className="bg-white border rounded-lg overflow-hidden">
                        <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50"
                          onClick={() => setExpandedSessionTrips(prev => ({...prev, [idx]: !prev[idx]}))}>
                          <div className="flex items-center gap-3">
                            {expandedSessionTrips[idx] ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                            <div>
                              <p className="font-semibold text-slate-800">Trip #{assignment.trip_number}</p>
                              <p className="text-xs text-slate-500">
                                Pincodes: <span className="font-medium text-slate-700">{assignment.pincodes_display || assignment.pincode || "—"}</span>
                              </p>
                              <p className="text-xs text-slate-500">Location: <span className="font-medium text-emerald-600">{assignment.location_name}</span></p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-blue-600 font-semibold">{assignment.shipment_count} shipments</span>
                          </div>
                        </div>
                        {expandedSessionTrips[idx] && assignment.sublocation_assignments && (
                          <div className="border-t bg-slate-50 p-4">
                            <div className="max-h-64 overflow-y-auto">
                              <table className="w-full text-xs">
                                <thead className="bg-slate-100 sticky top-0">
                                  <tr>
                                    <th className="px-3 py-2 text-left font-semibold text-slate-600">Seq</th>
                                    <th className="px-3 py-2 text-left font-semibold text-slate-600">Shipment ID</th>
                                    <th className="px-3 py-2 text-left font-semibold text-slate-600">Package ID</th>
                                    <th className="px-3 py-2 text-left font-semibold text-slate-600">Reference ID</th>
                                    <th className="px-3 py-2 text-left font-semibold text-slate-600">LBN</th>
                                    <th className="px-3 py-2 text-left font-semibold text-slate-600">Pincode</th>
                                    <th className="px-3 py-2 text-left font-semibold text-slate-600">Address</th>
                                    <th className="px-3 py-2 text-left font-semibold text-slate-600">Slot</th>
                                    <th className="px-3 py-2 text-center font-semibold text-slate-600">Map</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200">
                                  {assignment.sublocation_assignments.map((sub, subIdx) => {
                                    const mapLink = getMapLink(sub);
                                    return (
                                      <tr key={subIdx} className="hover:bg-white">
                                        <td className="px-3 py-2 text-slate-500">{sub.sequence}</td>
                                        <td className="px-3 py-2 font-mono text-blue-600">{sub.shipment_code || "—"}</td>
                                        <td className="px-3 py-2 font-mono text-slate-600">{sub.package_id || "—"}</td>
                                        <td className="px-3 py-2 font-mono text-slate-600 max-w-[150px] truncate" title={sub.reference_id}>{sub.reference_id || "—"}</td>
                                        <td className="px-3 py-2 font-mono text-purple-600">{sub.lbn || "—"}</td>
                                        <td className="px-3 py-2 font-mono text-orange-600">{sub.drop_pincode || sub.dest_address?.pincode || "—"}</td>
                                        <td className="px-3 py-2 text-slate-600 max-w-[200px] truncate" title={formatAddress(sub.dest_address)}>
                                          {formatAddress(sub.dest_address)}
                                        </td>
                                        <td className="px-3 py-2">
                                          <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700">{sub.sublocation_name}</Badge>
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                          {mapLink ? (
                                            <a href={mapLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center text-blue-500 hover:text-blue-700">
                                              <Navigation className="w-4 h-4" />
                                            </a>
                                          ) : (
                                            <span className="text-slate-300">—</span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedSession(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scan Modal */}
      <Dialog open={showScanModal} onOpenChange={setShowScanModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanLine className="w-5 h-5 text-emerald-600" /> Inward Scanning
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {inwardProgress && (
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-slate-700">Progress</span>
                  <span className="text-sm font-semibold text-emerald-600">
                    {inwardProgress.inwarded_count} / {inwardProgress.total_shipments} ({inwardProgress.progress_percent}%)
                  </span>
                </div>
                <div className="w-full h-3 bg-slate-200 rounded-full">
                  <div className="h-full bg-emerald-500 rounded-full transition-all duration-300"
                    style={{ width: `${inwardProgress.progress_percent}%` }} />
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Label className="text-sm font-semibold">Scan Barcode / Enter Code</Label>
              <div className="flex gap-2">
                <Input value={scanCode} onChange={(e) => setScanCode(e.target.value)} placeholder="Shipment ID, Package ID, or LBN" className="flex-1"
                  onKeyDown={(e) => e.key === "Enter" && handleScan()} autoFocus />
                <Button onClick={handleScan} disabled={scanning || !scanCode.trim()} className="bg-emerald-600 hover:bg-emerald-700">
                  {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />} Scan
                </Button>
              </div>
            </div>

            {scanResult && (
              <div className={`p-4 rounded-lg ${scanResult.success ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`}>
                {scanResult.success ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-8 h-8 text-emerald-600" />
                      <div>
                        <p className="font-semibold text-emerald-800">Inwarded Successfully!</p>
                        <p className="text-sm text-emerald-600">Shipment: {scanResult.shipment_id}</p>
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-md border border-emerald-100">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-slate-500">Location</p>
                          <p className="font-semibold text-emerald-700 text-lg">{scanResult.location_name}</p>
                        </div>
                        <div>
                          <p className="text-slate-500">Slot</p>
                          <p className="font-semibold text-emerald-700 text-lg">{scanResult.sublocation_name}</p>
                        </div>
                      </div>
                    </div>
                    <Button onClick={() => setScanResult(null)} className="w-full bg-emerald-600 hover:bg-emerald-700">
                      <Check className="w-4 h-4 mr-2" /> OK - Scan Next
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <XCircle className="w-8 h-8 text-red-600" />
                    <div>
                      <p className="font-semibold text-red-800">Scan Failed</p>
                      <p className="text-sm text-red-600">{scanResult.error}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {inwardProgress?.pending_shipments?.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Pending Shipments (Bulk Mode)</Label>
                  {selectedForBulk.length > 0 && (
                    <Button size="sm" onClick={handleBulkInward} disabled={bulkInwarding} className="bg-emerald-600 hover:bg-emerald-700">
                      {bulkInwarding ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                      Inward {selectedForBulk.length} Selected
                    </Button>
                  )}
                </div>
                <div className="max-h-48 overflow-y-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">
                          <Checkbox checked={selectedForBulk.length === inwardProgress.pending_shipments.length}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedForBulk(inwardProgress.pending_shipments.map(s => s.shipment_id));
                              } else {
                                setSelectedForBulk([]);
                              }
                            }} />
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Shipment</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Location</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Slot</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {inwardProgress.pending_shipments.map((s) => (
                        <tr key={s.shipment_id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 align-middle">
                            <Checkbox checked={selectedForBulk.includes(s.shipment_id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedForBulk([...selectedForBulk, s.shipment_id]);
                                } else {
                                  setSelectedForBulk(selectedForBulk.filter(id => id !== s.shipment_id));
                                }
                              }} />
                          </td>
                          <td className="px-4 py-3 align-middle font-mono text-blue-600">{s.shipment_code}</td>
                          <td className="px-4 py-3 align-middle">{s.location_name}</td>
                          <td className="px-4 py-3 align-middle"><Badge variant="outline" className="text-xs">{s.sublocation_name}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {inwardProgress?.progress_percent === 100 && (
              <div className="bg-emerald-50 p-4 rounded-lg text-center">
                <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
                <p className="font-semibold text-emerald-800">All Shipments Inwarded!</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowScanModal(false); fetchAllInwardSessions(); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PharmeasyLayout>
  );
};

export default PharmeasyHubOps;
